import { useRef, useState } from 'react'
import {
  Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Drawer, IconButton, ListSubheader,
  MenuItem, Select, Stack, Switch, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Workflow, WorkflowStep, TestRunResult, TestRunLogEntry } from '@cmms/api-client'

// No PageShell — custom layout for the builder
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { TRIGGER_MAP, TRIGGER_TYPES, TRIGGER_CONDITIONS, TRIGGER_HELP, TRIGGER_EXAMPLES, STEP_TYPES, STEP_MAP, STEP_HELP } from './constants'
import { ScheduleConfig } from './ScheduleConfig'
import { UserAvatar } from '../../components/UserAvatar'
import { SearchableSelect } from '../../components/SearchableSelect'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { tzWallToUtc, shortTzLabel } from '../../lib/tz-helpers'
import { formatSiteName } from '../../lib/siteDisplayName'
import { renderMessageBody, interpolatePreview } from '../../lib/messageFormat'

interface Props { workflowId: string; onBack: () => void }

export function WorkflowBuilder({ workflowId, onBack }: Props) {
  const qc = useQueryClient()
  const { formatDate } = useOrgSettings()
  const [tab, setTab] = useState<'workflow' | 'details' | 'test' | 'history'>('workflow')
  const [editName, setEditName] = useState(false)
  const [editDesc, setEditDesc] = useState(false)
  const [descDraft, setDescDraft] = useState('')
  const [drawerMode, setDrawerMode] = useState<'trigger' | 'add-step' | 'edit-step' | null>(null)
  const [drawerStepId, setDrawerStepId] = useState<string | null>(null)
  const [insertAfterOrder, setInsertAfterOrder] = useState(0)
  const [testPayload, setTestPayload] = useState('{\n  "wo_id": "WO-0001",\n  "title": "Test work order",\n  "priority": "high",\n  "status": "open"\n}')
  const [testResult, setTestResult] = useState<any>(null)
  const [testRunning, setTestRunning] = useState(false)
  const [testCompletedAt, setTestCompletedAt] = useState<string | null>(null)
  // Overrides for the test-run simulated context. Empty = backend picks
  // sensible defaults (first accessible site + first location under it).
  // Users override these when they need to test a specific location's
  // routing rules ("does the After-hours rule fire for Site X?").
  const [testSiteId, setTestSiteId] = useState<string>('')
  const [testLocationId, setTestLocationId] = useState<string>('')
  const [testPriority, setTestPriority] = useState<string>('high')

  const wfQ = useQuery({ queryKey: ['workflow', workflowId], queryFn: () => api.workflows.get(workflowId) })
  const runsQ = useQuery({ queryKey: ['workflow-runs', workflowId], queryFn: () => api.workflows.listRuns(workflowId), enabled: tab === 'history' || tab === 'test' })
  const wf = wfQ.data
  const steps = wf?.steps ?? []
  const runs = Array.isArray(runsQ.data) ? runsQ.data : []

  const toggleM = useMutation({ mutationFn: () => api.workflows.toggle(workflowId), onSuccess: () => qc.invalidateQueries({ queryKey: ['workflow', workflowId] }) })
  const updateM = useMutation({
    mutationFn: (body: any) => api.workflows.update(workflowId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workflow', workflowId] }); toast.success('Saved') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const addStepM = useMutation({
    mutationFn: (body: any) => api.workflows.addStep(workflowId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workflow', workflowId] }); setDrawerMode(null); toast.success('Step added') },
  })
  const updateStepM = useMutation({
    mutationFn: ({ stepId, body }: { stepId: string; body: any }) => api.workflows.updateStep(workflowId, stepId, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workflow', workflowId] }); setDrawerMode(null); toast.success('Updated') },
  })
  const removeStepM = useMutation({
    mutationFn: (stepId: string) => api.workflows.removeStep(workflowId, stepId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workflow', workflowId] }); toast.success('Removed') },
  })
  const reorderM = useMutation({
    mutationFn: (stepIds: string[]) => api.workflows.reorderSteps(workflowId, stepIds),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workflow', workflowId] }) },
    onError: () => toast.error('Failed to reorder'),
  })

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = steps.findIndex((s) => s.id === active.id)
    const newIdx = steps.findIndex((s) => s.id === over.id)
    if (oldIdx === -1 || newIdx === -1) return
    const reordered = [...steps]
    const [moved] = reordered.splice(oldIdx, 1)
    reordered.splice(newIdx, 0, moved!)
    reorderM.mutate(reordered.map((s) => s.id))
  }

  if (!wf) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>

  const trigger = TRIGGER_MAP[wf.trigger_type]
  const TriggerIcon = trigger?.icon ?? PlayArrowOutlinedIcon
  const triggerColor = trigger?.color ?? '#64748B'
  const woCode = `WF-${wf.id.slice(0, 6).toUpperCase()}`
  const isDraft = steps.length === 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Top breadcrumb bar ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 3, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }} onClick={onBack}>Automation</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>/</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }} onClick={onBack}>Workflows</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>/</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.primary', fontWeight: 600 }}>{wf.name}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Button size="small" variant="outlined" startIcon={<ScienceOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={async () => {
              try {
                const r = await api.workflows.simulate({ trigger_type: wf.trigger_type, title: 'Test', priority: 'medium', resource_id: workflowId })
                qc.invalidateQueries({ queryKey: ['workflow-runs', workflowId] })
                toast.success(r.runs_fired > 0 ? `${r.runs_fired} run(s) — check History` : 'No match')
              } catch (e: any) { toast.error(e?.response?.data?.message ?? 'Failed') }
            }}
            sx={{ fontSize: 12, fontWeight: 600, borderRadius: 1.5, textTransform: 'none', height: 30 }}>Test</Button>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: wf.is_active ? '#059669' : '#D97706' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: wf.is_active ? '#059669' : 'text.secondary' }}>
              {wf.is_active ? 'Active' : 'Inactive'}
            </Typography>
            <Switch size="small" checked={wf.is_active} onChange={() => toggleM.mutate()}
              sx={{ ml: 0.5, '& .MuiSwitch-thumb': { width: 14, height: 14 }, '& .MuiSwitch-switchBase': { p: '5px' } }} />
          </Stack>
          <Button size="small" variant="outlined" startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: 12, fontWeight: 600, borderRadius: 1.5, textTransform: 'none', height: 30 }}>Duplicate</Button>
          <Button size="small" variant="contained" startIcon={<SaveOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: 12, fontWeight: 600, borderRadius: 1.5, textTransform: 'none', height: 30 }}>Save</Button>
        </Stack>
      </Stack>

      {/* ── Header + Tabs ── */}
      <Box sx={{ px: 4, pt: 3, pb: 0, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1 }}>
          <IconButton size="small" onClick={onBack} sx={{ color: 'text.secondary', mt: 0.5 }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
          <Box sx={{ flex: 1 }}>
            {editName ? (
              <TextField value={wf.name} size="small" autoFocus fullWidth sx={{ '& .MuiOutlinedInput-root': { fontSize: 22, fontWeight: 700 } }}
                onBlur={(e) => { updateM.mutate({ name: e.target.value.trim() }); setEditName(false) }}
                onKeyDown={(e) => { if (e.key === 'Enter') { updateM.mutate({ name: (e.target as HTMLInputElement).value.trim() }); setEditName(false) } }} />
            ) : (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, cursor: 'pointer', letterSpacing: '-0.02em', '&:hover': { color: 'primary.main' } }} onClick={() => setEditName(true)}>{wf.name}</Typography>
                {isDraft ? <Chip label="Draft" size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
                  : wf.is_active ? <Chip icon={<Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: '#059669', ml: '8px !important' }} />} label="Active" size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#ECFDF5', color: '#059669', '& .MuiChip-icon': { mr: -0.25 } }} />
                  : <Chip label="Paused" size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#FFFBEB', color: '#D97706' }} />}
              </Stack>
            )}
            <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>
              {woCode} · {steps.length} steps · {runs.length} runs · Owned by {wf.created_by_name ?? 'System'}
            </Typography>
          </Box>
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, px: 2.5, fontSize: 13.5, fontWeight: 600, textTransform: 'none', letterSpacing: '-0.005em' },
            '& .MuiTabs-indicator': { height: 2.5, borderRadius: 99 } }}>
          <Tab value="workflow" label="Workflow" />
          <Tab value="details" label="Details" />
          <Tab value="test" label="Test run" />
          <Tab value="history" label={`History ${runs.length > 0 ? runs.length : ''}`} />
        </Tabs>
      </Box>

      {/* ── Content area ── */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

        {/* Main content (scrollable) */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 4, pt: 3, pb: 6 }}>
          <Box sx={{ maxWidth: 740, mx: 'auto' }}>

        {/* ═══ WORKFLOW TAB ═══ */}
        {tab === 'workflow' && (
          <Box sx={{
            position: 'relative', borderRadius: 3, overflow: 'hidden', minHeight: 'calc(100vh - 250px)',
            py: 5, px: 2,
            backgroundImage: (t) => t.palette.mode === 'light'
              ? 'radial-gradient(circle, #d1d5db 1px, transparent 1px)'
              : 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            border: '1px solid', borderColor: 'divider',
          }}>
            <Stack alignItems="center" spacing={0} sx={{ maxWidth: 460, mx: 'auto' }}>

              {/* Trigger card */}
              <Box sx={{ width: '100%' }}>
                <Typography sx={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'text.disabled', mb: 0.75, ml: 1 }}>TRIGGER</Typography>
                <Box onClick={() => { setDrawerMode('trigger'); setDrawerStepId(null) }}
                  sx={{ p: 2.5, borderRadius: 2.5, bgcolor: 'background.paper', border: '1px solid',
                    borderColor: drawerMode === 'trigger' ? triggerColor : 'divider',
                    cursor: 'pointer', transition: 'all 120ms',
                    boxShadow: drawerMode === 'trigger' ? `0 0 0 2px ${triggerColor}30` : 'none',
                    '&:hover': { borderColor: triggerColor } }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{ width: 42, height: 42, borderRadius: 99, bgcolor: `${triggerColor}14`, color: triggerColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <TriggerIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{trigger?.label ?? wf.trigger_type}</Typography>
                        <Chip label={trigger?.category ?? 'Other'} size="small"
                          sx={{ height: 20, fontSize: 9.5, fontWeight: 700, bgcolor: `${triggerColor}15`, color: triggerColor }} />
                      </Stack>
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
                        When this event occurs, the flow will start.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>

              <FlowLine />

              {/* Step cards with insert-between buttons */}
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                  {steps.map((step, i) => {
                    const m = STEP_MAP[step.step_type]; const c = m?.color ?? '#64748B'
                    const isSelected = drawerMode === 'edit-step' && drawerStepId === step.id

                    // Visual branching for if_else steps
                    if (step.step_type === 'if_else') {
                      const cfg = step.config ?? {}
                      const thenSteps: any[] = Array.isArray(cfg.then_steps) ? cfg.then_steps : []
                      const elseSteps: any[] = Array.isArray(cfg.else_steps) ? cfg.else_steps : []
                      return (
                        <Box key={step.id} sx={{ width: '100%' }}>
                          {/* Condition card */}
                          <Box onClick={() => { setDrawerMode('edit-step'); setDrawerStepId(step.id) }}
                            sx={{ p: 2.5, borderRadius: 2.5, bgcolor: 'background.paper', border: '1px solid',
                              borderColor: isSelected ? c : 'divider', cursor: 'pointer', transition: 'all 120ms',
                              boxShadow: isSelected ? `0 0 0 2px ${c}30` : 'none', '&:hover': { borderColor: c } }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Box sx={{ width: 38, height: 38, borderRadius: 99, border: '2px solid', borderColor: c,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: c }}>{i + 1}</Typography>
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{step.label || 'If / Else'}</Typography>
                                {cfg.field && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.15 }}>If {cfg.field} {cfg.operator ?? '='} {cfg.value}</Typography>}
                              </Box>
                            </Stack>
                          </Box>

                          {/* Y-fork visual */}
                          <Box sx={{ position: 'relative', mt: 0.5 }}>
                            {/* Fork lines */}
                            <Box sx={{ position: 'absolute', top: 0, left: '50%', width: 2, height: 16,
                              bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.12)' }} />
                            <Box sx={{ position: 'absolute', top: 16, left: '25%', right: '25%', height: 2,
                              bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.12)' }} />
                            <Box sx={{ position: 'absolute', top: 16, left: '25%', width: 2, height: 16,
                              bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.12)' }} />
                            <Box sx={{ position: 'absolute', top: 16, right: '25%', width: 2, height: 16,
                              bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.12)' }} />

                            {/* Two columns */}
                            <Stack direction="row" spacing={2} sx={{ pt: 4, px: 0 }}>
                              {/* THEN branch */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Chip label="THEN" size="small" sx={{ mb: 1.5, height: 22, fontSize: 10, fontWeight: 800,
                                  bgcolor: '#05966914', color: '#059669', '& .MuiChip-label': { px: 1.5 } }} />
                                <Stack spacing={1}>
                                  {thenSteps.map((sub: any, si: number) => {
                                    const sm = STEP_MAP[sub.step_type]; const sc = sm?.color ?? '#64748B'
                                    return (
                                      <Box key={si} sx={{ px: 2, py: 1.5, borderRadius: 2, bgcolor: 'background.paper',
                                        border: '1px solid', borderColor: 'divider', cursor: 'pointer',
                                        '&:hover': { borderColor: sc } }}
                                        onClick={() => { setDrawerMode('edit-step'); setDrawerStepId(step.id) }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                          <Box sx={{ width: 24, height: 24, borderRadius: 99, border: '2px solid', borderColor: sc,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: 10, fontWeight: 800, color: sc }}>{si + 1}</Typography>
                                          </Box>
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap>{sub.label || sm?.label || sub.step_type}</Typography>
                                          </Box>
                                        </Stack>
                                      </Box>
                                    )
                                  })}
                                  <Box onClick={() => { setDrawerMode('edit-step'); setDrawerStepId(step.id) }}
                                    sx={{ py: 1.5, textAlign: 'center', border: '1px dashed', borderColor: 'divider',
                                      borderRadius: 1.5, cursor: 'pointer', '&:hover': { borderColor: '#059669' } }}>
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                      {thenSteps.length === 0 ? 'Add THEN steps' : '+ Add more'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Box>

                              {/* ELSE branch */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Chip label="ELSE" size="small" sx={{ mb: 1.5, height: 22, fontSize: 10, fontWeight: 800,
                                  bgcolor: '#DC262614', color: '#DC2626', '& .MuiChip-label': { px: 1.5 } }} />
                                <Stack spacing={1}>
                                  {elseSteps.map((sub: any, si: number) => {
                                    const sm = STEP_MAP[sub.step_type]; const sc = sm?.color ?? '#64748B'
                                    return (
                                      <Box key={si} sx={{ px: 2, py: 1.5, borderRadius: 2, bgcolor: 'background.paper',
                                        border: '1px solid', borderColor: 'divider', cursor: 'pointer',
                                        '&:hover': { borderColor: sc } }}
                                        onClick={() => { setDrawerMode('edit-step'); setDrawerStepId(step.id) }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                          <Box sx={{ width: 24, height: 24, borderRadius: 99, border: '2px solid', borderColor: sc,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: 10, fontWeight: 800, color: sc }}>{si + 1}</Typography>
                                          </Box>
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap>{sub.label || sm?.label || sub.step_type}</Typography>
                                          </Box>
                                        </Stack>
                                      </Box>
                                    )
                                  })}
                                  <Box onClick={() => { setDrawerMode('edit-step'); setDrawerStepId(step.id) }}
                                    sx={{ py: 1.5, textAlign: 'center', border: '1px dashed', borderColor: 'divider',
                                      borderRadius: 1.5, cursor: 'pointer', '&:hover': { borderColor: '#DC2626' } }}>
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                      {elseSteps.length === 0 ? 'Add ELSE steps' : '+ Add more'}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Box>
                            </Stack>

                            {/* Merge lines */}
                            <Box sx={{ position: 'relative', height: 32 }}>
                              <Box sx={{ position: 'absolute', bottom: 16, left: '25%', width: 2, height: 16,
                                bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.12)' }} />
                              <Box sx={{ position: 'absolute', bottom: 16, right: '25%', width: 2, height: 16,
                                bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.12)' }} />
                              <Box sx={{ position: 'absolute', bottom: 16, left: '25%', right: '25%', height: 2,
                                bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.12)' }} />
                              <Box sx={{ position: 'absolute', bottom: 0, left: '50%', width: 2, height: 16,
                                bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.12)' }} />
                            </Box>
                          </Box>

                          {/* Insert-between + connector */}
                          <Stack alignItems="center" sx={{ position: 'relative', '&:hover .insert-btn': { opacity: 1 } }}>
                            <FlowLine />
                            <Tooltip title="Insert step here" arrow>
                              <IconButton className="insert-btn" size="small"
                                onClick={() => { setDrawerMode('add-step'); setInsertAfterOrder(step.step_order) }}
                                sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                                  width: 24, height: 24, bgcolor: 'primary.main', color: '#fff', opacity: 0,
                                  transition: 'opacity 120ms', '&:hover': { bgcolor: 'primary.dark' }, boxShadow: 2, zIndex: 1 }}>
                                <AddIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      )
                    }

                    return (
                      <SortableStepCard key={step.id} id={step.id}>
                        {(listeners, isDragging) => (
                          <Box sx={{ width: '100%' }}>
                            <Box onClick={() => { setDrawerMode('edit-step'); setDrawerStepId(step.id) }}
                              sx={{ p: 2.5, borderRadius: 2.5, bgcolor: 'background.paper', border: '1px solid',
                                borderColor: isSelected ? c : 'divider', cursor: 'pointer', transition: 'all 120ms',
                                boxShadow: isDragging ? '0 8px 24px -4px rgba(0,0,0,0.15)' : isSelected ? `0 0 0 2px ${c}30` : 'none',
                                '&:hover': { borderColor: c },
                                '&:hover .drag-handle': { opacity: 1 } }}>
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box className="drag-handle" {...listeners}
                                  sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', color: 'text.disabled', opacity: 0,
                                    transition: 'opacity 120ms', '&:active': { cursor: 'grabbing' }, flexShrink: 0, ml: -1 }}>
                                  <DragIndicatorIcon sx={{ fontSize: 20 }} />
                                </Box>
                                <Box sx={{ width: 38, height: 38, borderRadius: 99, border: '2px solid', borderColor: c,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: c }}>{i + 1}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{step.label || m?.label || step.step_type}</Typography>
                                  {stepHint(step) && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.15 }}>{stepHint(step)}</Typography>}
                                </Box>
                              </Stack>
                            </Box>

                            {/* Insert-between + connector */}
                            <Stack alignItems="center" sx={{ position: 'relative', '&:hover .insert-btn': { opacity: 1 } }}>
                              <FlowLine />
                              <Tooltip title="Insert step here" arrow>
                                <IconButton className="insert-btn" size="small"
                                  onClick={() => { setDrawerMode('add-step'); setInsertAfterOrder(step.step_order) }}
                                  sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                                    width: 24, height: 24, bgcolor: 'primary.main', color: '#fff', opacity: 0,
                                    transition: 'opacity 120ms', '&:hover': { bgcolor: 'primary.dark' }, boxShadow: 2, zIndex: 1 }}>
                                  <AddIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        )}
                      </SortableStepCard>
                    )
                  })}
                </SortableContext>
              </DndContext>

              {/* Add step button at the bottom */}
              <Tooltip title="Add a step" arrow>
                <IconButton onClick={() => { setDrawerMode('add-step'); setInsertAfterOrder(steps.length > 0 ? steps[steps.length - 1]!.step_order : 0) }}
                  sx={{ width: 40, height: 40, bgcolor: 'primary.main', color: '#fff',
                    '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.08)' },
                    boxShadow: 3, transition: 'transform 120ms' }}>
                  <AddIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>

            </Stack>
          </Box>
        )}

        {/* ═══ DETAILS TAB ═══ */}
        {tab === 'details' && (
          <Stack spacing={3}>
            <Card title="General" extra={<Typography sx={{ fontSize: 11, color: 'text.disabled' }}>Last updated {formatDate(wf.updated_at)}</Typography>}>
              <InfoRow label="Name">{wf.name}</InfoRow>
              <InfoRow label="Description">
                {editDesc ? (
                  <Stack spacing={1}>
                    <TextField value={descDraft} onChange={(e) => setDescDraft(e.target.value)} size="small" fullWidth multiline minRows={2} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => setEditDesc(false)} sx={{ color: 'text.secondary' }} disabled={updateM.isPending}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={() => { updateM.mutate({ description: descDraft }); setEditDesc(false) }} disabled={updateM.isPending}>{updateM.isPending ? 'Saving…' : 'Save'}</Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: 13, color: wf.description ? 'text.primary' : 'text.disabled' }}>{wf.description || 'No description'}</Typography>
                    <IconButton size="small" onClick={() => { setDescDraft(wf.description ?? ''); setEditDesc(true) }}><EditOutlinedIcon sx={{ fontSize: 13 }} /></IconButton>
                  </Stack>
                )}
              </InfoRow>
              <InfoRow label="Owner"><Typography sx={{ fontSize: 13 }}>{wf.created_by_name ?? 'System'}</Typography></InfoRow>
              <InfoRow label="Status">
                <Chip label={wf.is_active ? 'Active' : 'Paused'} size="small"
                  sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: wf.is_active ? '#ECFDF5' : '#FFFBEB', color: wf.is_active ? '#059669' : '#D97706' }} />
              </InfoRow>
              <InfoRow label="Customer scope">
                <Box sx={{ minWidth: 280, maxWidth: 360 }}>
                  <CustomerScopePicker
                    mode="catalog"
                    value={wf.customer_id ?? null}
                    onChange={(id) => updateM.mutate({ customer_id: id })}
                    helperText="All = workflow fires for any customer. Pick one to scope it."
                    size="small"
                  />
                </Box>
              </InfoRow>
              <InfoRow label="Workflow ID"><Typography sx={{ fontSize: 12.5, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>{woCode}</Typography></InfoRow>
            </Card>

            <Card title="Trigger" extra={<Button size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 13 }} />} onClick={() => { setTab('workflow'); setDrawerMode('trigger') }} sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary' }}>Edit</Button>}>
              <InfoRow label="Event"><Typography sx={{ fontSize: 13 }}>{trigger?.label ?? wf.trigger_type}</Typography></InfoRow>
              <InfoRow label="Category"><Chip label={trigger?.category ?? 'Other'} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: `${triggerColor}12`, color: triggerColor }} /></InfoRow>
              <InfoRow label="Conditions">
                {Object.keys(wf.trigger_config ?? {}).filter(k => k !== 'frequency' && wf.trigger_config[k]).length > 0 ? (
                  <Typography sx={{ fontSize: 12.5, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>
                    {Object.entries(wf.trigger_config ?? {}).filter(([k, v]) => v && k !== 'frequency').map(([k, v]) => `${k} = "${v}"`).join(' AND ')}
                  </Typography>
                ) : <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>No conditions</Typography>}
              </InfoRow>
            </Card>
          </Stack>
        )}

          </Box>{/* close maxWidth wrapper */}

        {/* ═══ TEST RUN TAB ═══ */}
        {tab === 'test' && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            {/* Left — Visual flow preview + run button */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Workflow flow</Typography>
                <Chip label={wf.is_active ? 'Active' : 'Inactive'} size="small"
                  sx={{ height: 20, fontSize: 10, fontWeight: 700,
                    bgcolor: wf.is_active ? '#05966914' : 'action.hover',
                    color: wf.is_active ? '#059669' : 'text.secondary' }} />
              </Stack>

              {/* Mini flow visualization */}
              <Stack alignItems="center" spacing={0} sx={{ mb: 3 }}>
                {/* Trigger */}
                <Box sx={{ width: '100%', p: 2, borderRadius: 2, bgcolor: `${triggerColor}08`, border: '1px solid', borderColor: `${triggerColor}30` }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 32, height: 32, borderRadius: 99, bgcolor: `${triggerColor}14`, color: triggerColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <TriggerIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{trigger?.label ?? wf.trigger_type}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Trigger</Typography>
                    </Box>
                    {testResult && (
                      <Box sx={{ ml: 'auto' }}>
                        <Chip label="Fired" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: `${triggerColor}14`, color: triggerColor }} />
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Steps */}
                {steps.map((step, i) => {
                  const sm = STEP_MAP[step.step_type]; const sc = sm?.color ?? '#64748B'
                  const logEntry = testResult?.log?.find((l: any) => l.type === 'step' && l.message?.includes(`Step ${step.step_order}`))
                  const stepStatus = logEntry?.status === 'error' ? 'error' : logEntry ? 'success' : testRunning ? (i === 0 ? 'running' : 'pending') : 'idle'
                  return (
                    <Box key={step.id} sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                        <Box sx={{ width: 2, height: 20, bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.1)' }} />
                      </Box>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid',
                        borderColor: stepStatus === 'error' ? '#DC2626' : stepStatus === 'success' ? '#059669' : 'divider',
                        transition: 'border-color 200ms' }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box sx={{ width: 28, height: 28, borderRadius: 99, border: '2px solid', borderColor: sc,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 800, color: sc }}>{i + 1}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{step.label || sm?.label || step.step_type}</Typography>
                            {stepHint(step) && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{stepHint(step)}</Typography>}
                          </Box>
                          {stepStatus === 'success' && <Chip label="Done" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#05966914', color: '#059669' }} />}
                          {stepStatus === 'error' && <Chip label="Failed" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#DC262614', color: '#DC2626' }} />}
                          {stepStatus === 'running' && <CircularProgress size={14} sx={{ color: sc }} />}
                        </Stack>
                      </Box>
                    </Box>
                  )
                })}
              </Stack>

              {/* Simulated context — lets the tester scope this test run
                  to a specific site/location so filters and Create WO
                  steps behave the same as they will in production.
                  Leave blank to let the backend pick your first site. */}
              <TestSimulationPanel
                siteId={testSiteId} onSiteChange={setTestSiteId}
                locationId={testLocationId} onLocationChange={setTestLocationId}
                priority={testPriority} onPriorityChange={setTestPriority}
              />

              {/* Run button */}
              <Button variant="contained" fullWidth disabled={testRunning}
                onClick={async () => {
                  setTestRunning(true); setTestResult(null)
                  try {
                    const result = await api.workflows.testRun(workflowId, {
                      title: 'Test work order',
                      priority: testPriority || 'high',
                      status: 'open',
                      wo_id: woCode,
                      // Only include site/location when the tester
                      // picked them — otherwise the backend's fallback
                      // logic runs and simulates a QR-like submission.
                      ...(testSiteId ? { site_id: testSiteId } : {}),
                      ...(testLocationId ? { location_id: testLocationId } : {}),
                    })
                    setTestResult(result)
                    setTestCompletedAt(new Date().toLocaleTimeString())
                    qc.invalidateQueries({ queryKey: ['workflow-runs', workflowId] })
                  } catch (e: any) { toast.error(e?.response?.data?.message ?? 'Test failed') }
                  finally { setTestRunning(false) }
                }}
                startIcon={testRunning ? <CircularProgress size={14} color="inherit" /> : <PlayArrowOutlinedIcon sx={{ fontSize: 16 }} />}
                sx={{ borderRadius: 2, fontWeight: 700, fontSize: 14, py: 1.5, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
                {testRunning ? 'Executing...' : 'Run test'}
              </Button>

              {testCompletedAt && testResult && (
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: testResult.status === 'completed' ? '#059669' : '#DC2626' }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: testResult.status === 'completed' ? '#059669' : '#DC2626' }}>
                    {testResult.status === 'completed' ? 'All steps passed' : 'Execution failed'} · {testResult.duration}ms · {testCompletedAt}
                  </Typography>
                </Stack>
              )}
            </Box>

            {/* Right — Execution log */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Execution log</Typography>
                {testResult && (
                  <Chip label={testResult.status === 'completed' ? 'Passed' : 'Failed'} size="small"
                    sx={{ height: 22, fontSize: 10.5, fontWeight: 700,
                      bgcolor: testResult.status === 'completed' ? '#ECFDF5' : '#FEF2F2',
                      color: testResult.status === 'completed' ? '#059669' : '#DC2626' }} />
                )}
              </Stack>

              {!testResult && !testRunning ? (
                <Box sx={{ py: 8, textAlign: 'center', borderRadius: 2.5, border: '1px dashed', borderColor: 'divider',
                  bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                  <PlayArrowOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled', opacity: 0.3, mb: 1 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>No execution yet</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>Click <strong>Run test</strong> to see the step-by-step execution log here.</Typography>
                </Box>
              ) : testRunning ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <CircularProgress size={28} sx={{ mb: 1.5 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>Executing workflow...</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>Running steps sequentially</Typography>
                </Box>
              ) : testResult && (
                <Stack spacing={0}>
                  {(testResult.log as TestRunLogEntry[]).map((entry, i) => {
                    const isError = entry.status === 'error'
                    const isComplete = entry.type === 'complete'
                    const dotColor = isError ? '#DC2626' : isComplete ? '#059669' : entry.type === 'trigger' ? triggerColor : '#3B82F6'

                    return (
                      <Stack key={i} direction="row" spacing={1.5} sx={{ position: 'relative' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18, flexShrink: 0, pt: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: dotColor, flexShrink: 0,
                            boxShadow: `0 0 0 3px ${dotColor}20` }} />
                          {i < testResult.log.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mt: 0.5 }} />}
                        </Box>
                        <Box sx={{ pb: 2.5, flex: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: isError ? '#DC2626' : 'text.primary' }}>
                            {entry.message}
                          </Typography>
                          {entry.detail && (
                            <Typography sx={{ fontSize: 12, mt: 0.25,
                              color: isError ? '#DC2626' : isComplete ? '#059669' : 'text.secondary',
                              fontWeight: isComplete ? 600 : 400 }}>
                              {entry.detail}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.35 }}>
                            +{entry.time}ms
                          </Typography>
                        </Box>
                      </Stack>
                    )
                  })}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
        {/* ═══ HISTORY TAB ═══ */}
        {tab === 'history' && <HistorySection workflowId={workflowId} runs={runs} formatDate={formatDate} />}

        </Box>

      </Box>

      {/* ── Right-side Drawer ── */}
      <Drawer anchor="right" open={!!drawerMode && tab === 'workflow'}
        onClose={() => { setDrawerMode(null); setDrawerStepId(null) }}
        variant="temporary"
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, borderLeft: '1px solid', borderColor: 'divider',
          boxShadow: '0 0 40px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column' } }}>

          {/* Drawer header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between"
            sx={{ px: 3.5, py: 2.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {(() => {
                if (drawerMode === 'trigger') {
                  return <Avatar sx={{ width: 36, height: 36, bgcolor: `${triggerColor}14`, color: triggerColor }}><TriggerIcon sx={{ fontSize: 18 }} /></Avatar>
                }
                if (drawerMode === 'edit-step' && drawerStepId) {
                  const s = steps.find(s => s.id === drawerStepId); const m = s ? STEP_MAP[s.step_type] : null
                  const c = m?.color ?? '#64748B'; const I = m?.icon ?? PlayArrowOutlinedIcon
                  return <Avatar sx={{ width: 36, height: 36, bgcolor: `${c}14`, color: c }}><I sx={{ fontSize: 18 }} /></Avatar>
                }
                return <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', color: '#fff' }}><AddIcon sx={{ fontSize: 18 }} /></Avatar>
              })()}
              <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>
                {drawerMode === 'trigger' ? 'Configure trigger' : drawerMode === 'add-step' ? 'Add a step' : `Configure: ${(() => { const s = steps.find(s => s.id === drawerStepId); const m = s ? STEP_MAP[s.step_type] : null; return s?.label || m?.label || 'Step' })()}`}
              </Typography>
            </Stack>
            <IconButton onClick={() => { setDrawerMode(null); setDrawerStepId(null) }}
              sx={{ color: 'text.secondary' }}>
              <Box component="span" sx={{ fontSize: 22, lineHeight: 1 }}>×</Box>
            </IconButton>
          </Stack>

          {/* Trigger config */}
          {drawerMode === 'trigger' && (
            <TriggerDrawerContent wf={wf}
              onSave={(t, c) => { updateM.mutate({ trigger_type: t, trigger_config: c }); setDrawerMode(null) }}
              onCancel={() => setDrawerMode(null)} />
          )}

          {/* Add step */}
          {drawerMode === 'add-step' && (
            <AddStepDrawerContent
              workflowId={workflowId}
              triggerType={wf.trigger_type}
              customerId={wf.customer_id ?? undefined}
              onAdd={(body) => { addStepM.mutate({ ...body, step_order: insertAfterOrder + 1 }); setDrawerMode(null) }}
              onCancel={() => setDrawerMode(null)} />
          )}

          {/* Edit step */}
          {drawerMode === 'edit-step' && drawerStepId && (() => {
            const step = steps.find(s => s.id === drawerStepId)
            if (!step) return null
            return <EditStepDrawerContent step={step} workflowId={workflowId}
              triggerType={wf.trigger_type}
              customerId={wf.customer_id ?? undefined}
              onSave={(body) => { updateStepM.mutate({ stepId: step.id, body }); setDrawerMode(null) }}
              onDelete={() => { removeStepM.mutate(step.id); setDrawerMode(null) }}
              onCancel={() => { setDrawerMode(null); setDrawerStepId(null) }} />
          })()}
      </Drawer>
    </Box>
  )
}

// ─── Drawer content components ──────────────────────────────────────────────

function TriggerDrawerContent({ wf, onSave, onCancel }: { wf: Workflow; onSave: (t: string, c: Record<string, any>) => void; onCancel: () => void }) {
  const [sel, setSel] = useState(wf.trigger_type)
  const [cfg, setCfg] = useState<Record<string, any>>(wf.trigger_config ?? {})
  const conditions = TRIGGER_CONDITIONS[sel] ?? []
  const example = TRIGGER_EXAMPLES[sel]
  const selDef = TRIGGER_TYPES.find(t => t.key === sel)

  // Same queries + helpers as the dialog / wizard variants. Keeps the
  // three surfaces in behavioural sync — every place a user picks a
  // site or location gets a real dropdown scoped to their access.
  const needsSitePicker = conditions.some((c) => c.type === 'site' || c.type === 'location')
  const sitesQ = useQuery({
    queryKey: ['sites'], queryFn: () => api.sites.list(),
    staleTime: 5 * 60_000, enabled: needsSitePicker,
  })
  const sites: any[] = Array.isArray(sitesQ.data) ? sitesQ.data : []
  const filterSite = cfg.site_id
  const locationsQ = useQuery({
    queryKey: ['locations', filterSite],
    queryFn: () => api.locations.list(filterSite),
    enabled: Boolean(filterSite && conditions.some((c) => c.type === 'location')),
    staleTime: 60_000,
  })
  const locations: any[] = Array.isArray(locationsQ.data) ? locationsQ.data : []
  const setCondKey = (k: string, v: any) => {
    const n = { ...cfg }
    if (v === '' || v === null || v === undefined) delete n[k]
    else n[k] = v
    if (k === 'site_id' && !v) delete n.location_id
    setCfg(n)
  }
  const timeWindow = (cfg.time_window && typeof cfg.time_window === 'object')
    ? cfg.time_window : { start: '', end: '', tz: '' }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
    <Box sx={{ flex: 1, overflow: 'auto', scrollbarWidth: 'thin', px: 3.5, py: 3 }}>
    <Stack spacing={3.5}>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', mb: 2 }}>Trigger event</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.25 }}>
          {TRIGGER_TYPES.map((t) => {
            const isSelected = sel === t.key
            return (
              <Box key={t.key} onClick={() => { setSel(t.key); if (t.key !== wf.trigger_type) setCfg({}) }}
                sx={{ px: 2, py: 1.75, borderRadius: 2, cursor: 'pointer', border: '2px solid',
                  borderColor: isSelected ? t.color : 'divider', bgcolor: isSelected ? `${t.color}06` : 'transparent',
                  transition: 'all 100ms', '&:hover': { borderColor: t.color, bgcolor: `${t.color}04` } }}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: `${t.color}14`, color: t.color }}>
                    <t.icon sx={{ fontSize: 15 }} />
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{t.label}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{t.category}</Typography>
                  </Box>
                </Stack>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Example flow preview */}
      {example && selDef && (
        <Box sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
          bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Avatar sx={{ width: 24, height: 24, bgcolor: `${selDef.color}14`, color: selDef.color }}>
              <selDef.icon sx={{ fontSize: 13 }} />
            </Avatar>
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{example.useCase}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.5, mb: 2 }}>{example.description}</Typography>

          <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 1 }}>Example flow</Typography>
          <Stack alignItems="center" spacing={0}>
            <MiniFlowChip label="Trigger" sublabel={selDef.label} color={selDef.color} />
            {example.exampleFlow.map((s, i) => {
              const sd = STEP_MAP[s.type]
              return (<Box key={i} sx={{ width: '100%' }}>
                <Box sx={{ width: 1.5, height: 10, bgcolor: 'divider', mx: 'auto' }} />
                <MiniFlowChip label={s.label} color={sd?.color ?? '#64748B'} icon={sd?.icon} num={i + 1} />
              </Box>)
            })}
          </Stack>
        </Box>
      )}

      {/* Conditions / Schedule */}
      {sel === 'schedule' ? (
        <Box><Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', mb: 1 }}>Schedule</Typography><ScheduleConfig config={cfg} onChange={setCfg} /></Box>
      ) : conditions.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', mb: 2 }}>Filter conditions</Typography>
          <Stack spacing={2.5}>
            {conditions.map((c) => (
              <Box key={c.key}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>{c.label}</Typography>
                {c.type === 'select' ? (
                  <Select value={cfg[c.key] ?? ''} onChange={(e) => setCondKey(c.key, e.target.value)} size="medium" fullWidth displayEmpty>
                    {(c.options ?? []).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                ) : c.type === 'checklist' ? (
                  <ChecklistConditionSelect value={cfg[c.key] ?? ''} onChange={(v) => setCondKey(c.key, v)} />
                ) : c.type === 'site' ? (
                  <SearchableSelect
                    value={cfg[c.key] ?? ''}
                    onChange={(id) => setCondKey(c.key, id)}
                    options={sites}
                    getValue={(s: any) => s.id}
                    getLabel={formatSiteName}
                    placeholder="Any site"
                  />
                ) : c.type === 'location' ? (
                  <SearchableSelect
                    value={cfg[c.key] ?? ''}
                    onChange={(id) => setCondKey(c.key, id)}
                    options={locations}
                    getValue={(l: any) => l.id}
                    getLabel={(l: any) => l.name}
                    placeholder={filterSite ? 'Any location' : 'Pick a site first'}
                  />
                ) : c.type === 'time_window' ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      type="time" size="small" label="From"
                      value={timeWindow.start ?? ''}
                      onChange={(e) => setCondKey(c.key, { ...timeWindow, start: e.target.value })}
                      InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                    />
                    <TextField
                      type="time" size="small" label="To"
                      value={timeWindow.end ?? ''}
                      onChange={(e) => setCondKey(c.key, { ...timeWindow, end: e.target.value })}
                      InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small" label="Timezone"
                      value={timeWindow.tz ?? ''}
                      onChange={(e) => setCondKey(c.key, { ...timeWindow, tz: e.target.value })}
                      placeholder="Asia/Kolkata" sx={{ flex: 1.2 }}
                    />
                  </Stack>
                ) : c.type === 'asset_soft_point' ? (
                  <AssetSoftPointPicker
                    assetId={cfg.asset_id ?? ''}
                    softPointId={cfg.soft_point_id ?? ''}
                    onChange={(patch) => setCfg((prev) => ({ ...prev, ...patch }))}
                  />
                ) : <TextField value={cfg[c.key] ?? ''} onChange={(e) => setCondKey(c.key, e.target.value)} size="medium" fullWidth />}
                {c.helper && (
                  <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.75 }}>{c.helper}</Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

    </Stack>
    </Box>
    <Box sx={{ px: 3.5, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
        <Button onClick={onCancel} variant="outlined" sx={{ fontSize: 14, fontWeight: 600, textTransform: 'none', borderRadius: 2, px: 3, height: 44 }}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(sel, cfg)}
          startIcon={<Box component="span" sx={{ fontSize: 13 }}>✓</Box>}
          sx={{ borderRadius: 2, fontWeight: 700, fontSize: 14, px: 3, height: 44 }}>Save trigger</Button>
      </Stack>
    </Box>
    </Box>
  )
}

/**
 * Sensible per-step defaults keyed off the workflow's trigger. Ensures
 * the "obvious" behaviours of common trigger→step combos happen without
 * requiring the admin to flip five checkboxes:
 *
 *   request.submitted → Create WO:
 *     - link_to_request (so the request status flips to Approved and
 *       the Approve/Decline buttons disappear)
 *     - copy_request_attachments (so QR photos land on the WO)
 *     - inherit_description (so the WO description matches the request)
 *     - reporter_id = __requester (so close-emails go to the submitter)
 *
 * Existing steps are never touched — this only runs when a step is
 * added fresh from the drawer.
 */
function seedStepDefaults(stepType: string, triggerType?: string): Record<string, any> {
  const cfg: Record<string, any> = {}
  if ((stepType === 'create_work_order' || stepType === 'create_task') && triggerType === 'request.submitted') {
    cfg.link_to_request = true
    cfg.inherit_description = true
    cfg.inherit_priority = true
    cfg.reporter_id = '__requester'
    cfg.copy_request_attachments = true
  }
  return cfg
}

function isStepValid(stepType: string, config: Record<string, any>): boolean {
  switch (stepType) {
    case 'approval': return !!config.approver_id
    case 'notification': return !!config.channel && !!config.notify_id
    case 'email': return !!(config.to?.trim()) && !!(config.subject?.trim())
    case 'assign': return !!(config.assignee_id || config.team_id)
    case 'update_field': return !!config.field && !!(config.value?.toString().trim())
    case 'create_work_order': return !!(config.title?.trim())
    case 'create_task': return !!(config.title?.trim())
    case 'condition': return !!config.field && !!(config.value?.toString().trim())
    case 'delay': return !!config.duration
    case 'attach_checklist': return !!config.template_id
    case 'webhook': return !!(config.url?.trim())
    case 'if_else': return !!config.field
    case 'switch': return !!config.field && Array.isArray(config.cases) && config.cases.length > 0
    case 'copy_request_attachments': return true
    case 'update_request_status': return !!config.action
    case 'escalation_timer': return !!config.wait_duration
    case 'repeat_until': return !!config.interval_duration
    default: return true
  }
}

function AddStepDrawerContent({ onAdd, onCancel, workflowId, triggerType, customerId }: { onAdd: (body: any) => void; onCancel: () => void; workflowId: string; triggerType: string; customerId?: string }) {
  const [stepType, setStepType] = useState('')
  const [label, setLabel] = useState('')
  const [config, setConfig] = useState<Record<string, any>>({})

  const renderStepCard = (s: typeof STEP_TYPES[0]) => {
    const isSelected = stepType === s.key
    return (
      <Box key={s.key} onClick={() => {
          setStepType(s.key); setLabel(s.label)
          // Seed sensible defaults for the common request.submitted →
          // Create WO pipeline so users don't have to hunt through
          // checkboxes to get the obvious behaviour. Existing steps are
          // unaffected — this only runs at add-time.
          setConfig(seedStepDefaults(s.key, triggerType))
        }}
        sx={{ px: 2, py: 1.75, borderRadius: 2, cursor: 'pointer', border: '2px solid',
          borderColor: isSelected ? s.color : 'divider', bgcolor: isSelected ? `${s.color}06` : 'transparent',
          transition: 'all 100ms', '&:hover': { borderColor: s.color, bgcolor: `${s.color}04` } }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: `${s.color}14`, color: s.color }}>
            <s.icon sx={{ fontSize: 17 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{s.label}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>{s.description}</Typography>
          </Box>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
    <Box sx={{ flex: 1, overflow: 'auto', scrollbarWidth: 'thin', px: 3.5, py: 3 }}>
    <Stack spacing={3.5}>
      <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>Pick a step type to insert. You can reorder or delete later.</Typography>

      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 1.25 }}>ACTIONS</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.25, mb: 2.5 }}>
          {STEP_TYPES.filter(s => !['condition', 'if_else', 'delay', 'escalation_timer', 'repeat_until', 'approval'].includes(s.key)).map(renderStepCard)}
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mb: 1.25 }}>CONTROL FLOW</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.25 }}>
          {STEP_TYPES.filter(s => ['condition', 'if_else', 'delay', 'escalation_timer', 'repeat_until', 'approval'].includes(s.key)).map(renderStepCard)}
        </Box>
      </Box>

      {stepType && (
        <>
          {STEP_HELP[stepType] && (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'light' ? '#F0F9FF' : 'rgba(59,130,246,0.06)',
              border: '1px solid', borderColor: (t) => t.palette.mode === 'light' ? '#BAE6FD' : 'rgba(59,130,246,0.2)' }}>
              <Typography sx={{ fontSize: 12.5, color: (t) => t.palette.mode === 'light' ? '#0369A1' : '#7DD3FC', lineHeight: 1.5 }}>💡 {STEP_HELP[stepType]}</Typography>
            </Box>
          )}
          <TextField label="Step label" value={label} onChange={(e) => setLabel(e.target.value)} size="medium" fullWidth />
          <StepConfigFields stepType={stepType} config={config} onChange={setConfig} workflowId={workflowId} triggerType={triggerType} customerId={customerId} />
        </>
      )}
    </Stack>
    </Box>
    <Box sx={{ px: 3.5, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
        <Button onClick={onCancel} variant="outlined" sx={{ fontSize: 14, fontWeight: 600, textTransform: 'none', borderRadius: 2, px: 3, height: 44 }}>Cancel</Button>
        {stepType && (
          <Button variant="contained" onClick={() => onAdd({ step_type: stepType, label, config })}
            disabled={!isStepValid(stepType, config)}
            startIcon={<Box component="span" sx={{ fontSize: 13 }}>✓</Box>}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: 14, px: 3, height: 44 }}>Add step</Button>
        )}
      </Stack>
    </Box>
    </Box>
  )
}

function EditStepDrawerContent({ step, onSave, onDelete, onCancel, workflowId, triggerType, customerId }: { step: WorkflowStep; onSave: (b: any) => void; onDelete: () => void; onCancel: () => void; workflowId: string; triggerType: string; customerId?: string }) {
  const [label, setLabel] = useState(step.label ?? '')
  const [config, setConfig] = useState<Record<string, any>>(step.config ?? {})
  const m = STEP_MAP[step.step_type]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', scrollbarWidth: 'thin', px: 3.5, py: 3 }}>
        <Stack spacing={3}>
          <StepConfigFields stepType={step.step_type} config={config} onChange={setConfig} workflowId={workflowId} stepId={step.id} triggerType={triggerType} customerId={customerId} />
          <Divider />
          <TextField label="Step label" value={label} onChange={(e) => setLabel(e.target.value)} size="medium" fullWidth
            helperText="A custom name for this step (optional)" />
        </Stack>
      </Box>
      <Box sx={{ px: 3.5, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="outlined" color="error" onClick={onDelete} startIcon={<DeleteOutlineIcon sx={{ fontSize: 15 }} />}
            sx={{ fontSize: 14, fontWeight: 600, textTransform: 'none', borderRadius: 2, px: 2.5, height: 44 }}>Delete step</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onCancel} variant="outlined"
            sx={{ fontSize: 14, fontWeight: 600, textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}>Cancel</Button>
          <Button variant="contained" onClick={() => onSave({ label, config })}
            disabled={!isStepValid(step.step_type, config)}
            startIcon={<Box component="span" sx={{ fontSize: 13 }}>✓</Box>}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: 14, px: 3, height: 44 }}>Save step</Button>
        </Stack>
      </Box>
    </Box>
  )
}

// ─── History section with sub-tabs ──────────────────────────────────────────

function HistorySection({ workflowId, runs, formatDate }: { workflowId: string; runs: any[]; formatDate: (d: string) => string }) {
  const [subTab, setSubTab] = useState<'runs' | 'audit'>('runs')

  const auditQ = useQuery({ queryKey: ['workflow-audit', workflowId], queryFn: () => api.workflows.listAuditLog(workflowId), enabled: subTab === 'audit' })
  const auditEntries = Array.isArray(auditQ.data) ? auditQ.data : []

  const ACTION_LABELS: Record<string, { label: string; color: string }> = {
    'workflow.created': { label: 'Created', color: '#059669' },
    'workflow.updated': { label: 'Updated', color: '#3B82F6' },
    'workflow.deleted': { label: 'Deleted', color: '#DC2626' },
    'workflow.activated': { label: 'Activated', color: '#059669' },
    'workflow.deactivated': { label: 'Paused', color: '#D97706' },
    'workflow.approval_step_added': { label: 'Approver added', color: '#8B5CF6' },
    'workflow.approval_step_removed': { label: 'Approver removed', color: '#DC2626' },
    'workflow.step_approved': { label: 'Step approved', color: '#059669' },
    'workflow.step_rejected': { label: 'Step rejected', color: '#DC2626' },
  }

  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)}
        sx={{ mb: 3, minHeight: 32, '& .MuiTab-root': { minHeight: 32, px: 2, fontSize: 12.5, fontWeight: 600, textTransform: 'none' } }}>
        <Tab value="runs" label={`Runs (${runs.length})`} />
        <Tab value="audit" label="Audit log" />
      </Tabs>

      {subTab === 'runs' && (
        runs.length > 0 ? <RunTable runs={runs} formatDate={formatDate} /> : (
          <Typography sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center', py: 6 }}>No runs yet.</Typography>
        )
      )}

      {subTab === 'audit' && (
        auditQ.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
        ) : auditEntries.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: 'text.disabled', textAlign: 'center', py: 6 }}>No audit entries yet.</Typography>
        ) : (
          <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Stack direction="row" sx={{ px: 2.5, py: 1, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
              <Typography sx={{ flex: 2, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Date</Typography>
              <Typography sx={{ flex: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Action</Typography>
              <Typography sx={{ flex: 2, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Changed by</Typography>
              <Typography sx={{ flex: 3, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Details</Typography>
            </Stack>
            {auditEntries.map((entry: any) => {
              const actionKey = entry.action ?? ''
              const actionMeta = ACTION_LABELS[actionKey] ?? { label: actionKey.split('.').pop() ?? actionKey, color: '#64748B' }
              const meta = entry.metadata ?? {}
              const details = Object.entries(meta).filter(([k]) => !['actor_email'].includes(k)).map(([k, v]) => `${k}: ${v}`).join(', ')

              return (
                <Stack key={entry.id} direction="row" alignItems="center"
                  sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ flex: 2, fontSize: 12.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                    {formatDate(entry.created_at)}
                  </Typography>
                  <Box sx={{ flex: 1.5 }}>
                    <Chip label={actionMeta.label} size="small"
                      sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: `${actionMeta.color}15`, color: actionMeta.color }} />
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <UserAvatar name={entry.actor_name} avatarUrl={entry.actor_avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} />
                      <Typography sx={{ fontSize: 12.5 }}>{entry.actor_name}</Typography>
                    </Stack>
                  </Box>
                  <Typography sx={{ flex: 3, fontSize: 12, color: 'text.secondary' }} noWrap>
                    {details || '—'}
                  </Typography>
                </Stack>
              )
            })}
          </Box>
        )
      )}
    </Box>
  )
}

function MiniFlowChip({ label, sublabel, color, icon: Icon, num }: {
  label: string; sublabel?: string; color: string; icon?: any; num?: number
}) {
  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ width: 20, height: 20, borderRadius: 1, bgcolor: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {Icon ? <Icon sx={{ fontSize: 11 }} /> : <Typography sx={{ fontSize: 8, fontWeight: 800 }}>{num ?? '▶'}</Typography>}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2 }} noWrap>{label}</Typography>
          {sublabel && <Typography sx={{ fontSize: 9.5, color: 'text.secondary' }} noWrap>{sublabel}</Typography>}
        </Box>
      </Stack>
    </Box>
  )
}

// ─── Flow components ────────────────────────────────────────────────────────



function StepAttachments({ attachments, onChange, workflowId, stepId }: { attachments: any[]; onChange: (atts: any[]) => void; workflowId?: string; stepId?: string }) {
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const wfId = workflowId ?? 'temp'
  const sId = stepId ?? 'temp'

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files || files.length === 0) return
    setUploading(true)
    const newAtts = [...attachments]
    for (const file of Array.from(files)) {
      try {
        const result = await api.workflows.uploadStepFile(wfId, sId, file)
        newAtts.push({ url: result.url, filename: result.filename, size: result.size, contentType: result.contentType })
      } catch { toast.error(`Failed to upload ${file.name}`) }
    }
    onChange(newAtts)
    setUploading(false)
    if (fileRef.current) fileRef.current.value = ''
  }

  function removeFile(idx: number) {
    const att = attachments[idx]
    if (att?.url) {
      api.workflows.deleteStepFile(wfId, sId, att.url).catch(() => {})
    }
    const next = [...attachments]
    next.splice(idx, 1)
    onChange(next)
  }

  return (
    <Box>
      {attachments.length > 0 && (
        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          {attachments.map((att, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}
              sx={{ px: 1.5, py: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: 'action.hover',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                  {(att.filename ?? '').split('.').pop()?.slice(0, 4)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 500 }} noWrap>{att.filename}</Typography>
                {att.size && <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{(att.size / 1024).toFixed(0)} KB</Typography>}
              </Box>
              <IconButton size="small" onClick={() => removeFile(i)}
                sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' }, p: 0.25 }}>
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <input ref={fileRef} type="file" multiple hidden onChange={handleFiles} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" />
      <Button size="small" variant="outlined" onClick={() => fileRef.current?.click()} disabled={uploading}
        sx={{ fontSize: 12, fontWeight: 600 }}>
        {uploading ? 'Uploading...' : attachments.length > 0 ? '+ Add more files' : 'Upload files'}
      </Button>
      <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>
        These files will be attached to the work order when it is auto-created.
      </Typography>
    </Box>
  )
}

// ─── Condition editor (shared by `condition` + `if_else` steps) ─────────────

const CONDITION_FIELD_GROUPS: Array<{ group: string; items: Array<{ key: string; label: string }> }> = [
  { group: 'Work order', items: [
    { key: 'priority',   label: 'Priority' },
    { key: 'status',     label: 'Status' },
    { key: 'work_type',  label: 'Work type' },
    { key: 'category',   label: 'Category' },
  ]},
  { group: 'Text search', items: [
    { key: 'title',       label: 'Title' },
    { key: 'description', label: 'Description' },
  ]},
  { group: 'Request context', items: [
    { key: 'source',              label: 'Source (intake channel)' },
    { key: 'submitted_by_email',  label: 'Submitter email' },
  ]},
  { group: 'Assignment', items: [
    { key: 'assigned_to_user_id', label: 'Assigned to' },
    { key: 'reporter_id',         label: 'Reporter' },
  ]},
  { group: 'Location', items: [
    { key: 'site_id',     label: 'Site' },
    { key: 'location_id', label: 'Location' },
  ]},
  { group: 'Time & date', items: [
    { key: '_time_context', label: 'Current time / day' },
  ]},
  { group: 'Soft point reading', items: [
    { key: 'value',          label: 'Value' },
    { key: 'previous_value', label: 'Previous value' },
    { key: 'recent_values',  label: 'Recent readings' },
  ]},
]

type FieldKind = 'enum' | 'text' | 'user' | 'entity' | 'time' | 'number' | 'recent_values'
function conditionFieldKind(field: string): FieldKind {
  if (['priority', 'status', 'work_type', 'category', 'source'].includes(field)) return 'enum'
  if (['title', 'description', 'submitted_by_email'].includes(field)) return 'text'
  if (['assigned_to_user_id', 'reporter_id'].includes(field)) return 'user'
  if (['site_id', 'location_id'].includes(field)) return 'entity'
  if (field === '_time_context') return 'time'
  if (['value', 'previous_value'].includes(field)) return 'number'
  if (field === 'recent_values') return 'recent_values'
  return 'text'
}

const OPERATOR_LABELS: Record<string, string> = {
  equals: 'equals', not_equals: 'does not equal',
  contains: 'contains', not_contains: 'does not contain',
  starts_with: 'starts with', ends_with: 'ends with',
  is_empty: 'is empty', is_not_empty: 'is not empty',
  in_list: 'is one of (list)', not_in_list: 'is not one of (list)',
  is_weekend: 'is a weekend', is_weekday: 'is a weekday',
  is_business_hours: 'is business hours', is_after_hours: 'is after hours',
  greater_than: 'is greater than', less_than: 'is less than',
  greater_than_or_equal: 'is greater than or equal to', less_than_or_equal: 'is less than or equal to',
  occurs_in_recent: 'occurred at least N times in the last M readings',
}

const OPS_BY_KIND: Record<FieldKind, string[]> = {
  enum:   ['equals', 'not_equals', 'in_list', 'not_in_list', 'is_empty', 'is_not_empty'],
  text:   ['contains', 'not_contains', 'starts_with', 'ends_with', 'equals', 'not_equals', 'is_empty', 'is_not_empty'],
  user:   ['equals', 'not_equals', 'in_list', 'not_in_list', 'is_empty', 'is_not_empty'],
  entity: ['equals', 'not_equals', 'in_list', 'not_in_list', 'is_empty', 'is_not_empty'],
  time:   ['is_business_hours', 'is_after_hours', 'is_weekend', 'is_weekday'],
  number: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal'],
  recent_values: ['occurs_in_recent'],
}

const OPS_WITHOUT_VALUE = new Set([
  'is_empty', 'is_not_empty', 'is_weekend', 'is_weekday',
])

// Which triggers populate each condition field in context. Used to warn
// the user when they pick a field the current workflow's trigger doesn't
// provide (e.g. "source" only exists on request.submitted). `'*'` = the
// field is always available, no trigger needed.
const FIELD_AVAILABILITY: Record<string, string[] | '*'> = {
  priority:              ['work_order.created', 'work_order.closed', 'work_order.status_changed', 'request.submitted'],
  status:                ['work_order.created', 'work_order.closed', 'work_order.status_changed', 'asset.status_changed'],
  work_type:             ['work_order.created', 'work_order.closed', 'work_order.status_changed'],
  category:              ['work_order.created', 'work_order.closed', 'work_order.status_changed'],
  title:                 ['work_order.created', 'work_order.closed', 'work_order.status_changed', 'request.submitted'],
  description:           ['request.submitted'],
  source:                ['request.submitted'],
  submitted_by_email:    ['request.submitted'],
  assigned_to_user_id:   ['work_order.created', 'work_order.closed', 'work_order.status_changed'],
  reporter_id:           ['request.submitted', 'work_order.created', 'work_order.closed', 'work_order.status_changed'],
  site_id:               ['work_order.created', 'work_order.closed', 'work_order.status_changed', 'request.submitted', 'asset.status_changed', 'asset.soft_point_reading'],
  location_id:           ['work_order.created', 'work_order.closed', 'work_order.status_changed', 'request.submitted', 'asset.soft_point_reading'],
  _time_context:         '*',
  value:                 ['asset.soft_point_reading'],
  previous_value:        ['asset.soft_point_reading'],
  recent_values:         ['asset.soft_point_reading'],
}

function isFieldAvailable(field: string, triggerType?: string): boolean {
  if (!triggerType) return true
  const avail = FIELD_AVAILABILITY[field]
  if (avail === '*') return true
  if (!avail) return true
  return avail.includes(triggerType)
}

// Human-friendly label for the trigger types that populate a field.
// Falls back to the raw trigger key when we don't have a definition.
function availabilityLabel(field: string): string {
  const avail = FIELD_AVAILABILITY[field]
  if (avail === '*') return 'Always available — no trigger needed'
  if (!avail) return 'Available on any trigger'
  const labels = avail.map((k) => TRIGGER_MAP[k]?.label ?? k)
  return `Requires trigger: ${labels.join(' · ')}`
}

function ConditionEditor({ config, onChange, users, categories, sites, locations, triggerType }: {
  config: Record<string, any>
  onChange: (next: Record<string, any>) => void
  users: any[]
  categories: any[]
  sites: any[]
  locations: any[]
  triggerType?: string
}) {
  const field = config.field ?? ''
  const kind = conditionFieldKind(field)
  const validOps = OPS_BY_KIND[kind] ?? ['equals', 'not_equals']
  const operator = validOps.includes(config.operator) ? config.operator : validOps[0]

  const setField = (f: string) => {
    // Field-type change resets value + operator so we don't leave a
    // stale (op, value) that doesn't apply to the new field kind.
    const nk = conditionFieldKind(f)
    const nextOps = OPS_BY_KIND[nk]
    const nextOp = nextOps[0]
    onChange({
      ...config,
      field: f,
      operator: nextOp,
      value: '',
      value_site_scope: undefined,
      timezone: config.timezone,
      times: undefined,
      window: undefined,
    })
  }
  const setOp = (o: string) => {
    onChange({ ...config, operator: o, value: OPS_WITHOUT_VALUE.has(o) ? '' : (config.value ?? '') })
  }
  const setValue = (v: any) => onChange({ ...config, value: v })

  const renderValue = () => {
    if (OPS_WITHOUT_VALUE.has(operator)) {
      return (
        <Box sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'action.hover', color: 'text.disabled', fontSize: 13, fontStyle: 'italic', flex: 1 }}>
          No value needed
        </Box>
      )
    }
    if (kind === 'time') {
      // Only business_hours + after_hours reach here (weekend/weekday
      // are no-value). Value shape: "HH:MM-HH:MM" — kept as plain text
      // to avoid pushing two DOM inputs into a single condition row.
      return (
        <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
          <TextField
            size="small"
            placeholder="09:00-18:00"
            value={config.value ?? ''}
            onChange={(e) => setValue(e.target.value)}
            sx={{ flex: 1 }}
            helperText="Business-hours window in HH:MM-HH:MM"
          />
          <TextField
            size="small" label="Timezone"
            value={config.timezone ?? ''}
            onChange={(e) => onChange({ ...config, timezone: e.target.value })}
            placeholder="Asia/Kolkata"
            sx={{ flex: 1 }}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      )
    }
    if (operator === 'occurs_in_recent') {
      // Value + threshold + window, all numeric. `window` blank means
      // "look at every reading we have" — see engine.js's evaluateCondition,
      // which defaults to `recent.length` when `config.window` is unset.
      return (
        <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
          <TextField
            type="number" size="small" label="Value" sx={{ flex: 1 }}
            value={config.value ?? ''}
            onChange={(e) => setValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="number" size="small" label="At least N times" sx={{ flex: 1 }}
            value={config.times ?? ''}
            onChange={(e) => onChange({ ...config, times: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="number" size="small" label="In last M readings" sx={{ flex: 1 }}
            value={config.window ?? ''}
            onChange={(e) => onChange({ ...config, window: e.target.value })}
            InputLabelProps={{ shrink: true }}
            placeholder="All"
          />
        </Stack>
      )
    }
    if (kind === 'number') return (
      <TextField
        type="number" size="small" fullWidth
        value={config.value ?? ''}
        onChange={(e) => setValue(e.target.value)}
        placeholder="e.g. 4"
      />
    )
    if (field === 'priority') return (
      <Select value={config.value ?? ''} onChange={(e) => setValue(e.target.value)} size="small" fullWidth displayEmpty>
        <MenuItem value="">Select value</MenuItem>
        <MenuItem value="low">Low</MenuItem>
        <MenuItem value="medium">Medium</MenuItem>
        <MenuItem value="high">High</MenuItem>
        <MenuItem value="critical">Critical</MenuItem>
      </Select>
    )
    if (field === 'status') return (
      <Select value={config.value ?? ''} onChange={(e) => setValue(e.target.value)} size="small" fullWidth displayEmpty>
        <MenuItem value="">Select value</MenuItem>
        <MenuItem value="open">Open</MenuItem>
        <MenuItem value="in_progress">In Progress</MenuItem>
        <MenuItem value="closed">Closed</MenuItem>
        <MenuItem value="resolved">Resolved</MenuItem>
      </Select>
    )
    if (field === 'work_type') return (
      <Select value={config.value ?? ''} onChange={(e) => setValue(e.target.value)} size="small" fullWidth displayEmpty>
        <MenuItem value="">Select value</MenuItem>
        <MenuItem value="reactive">Reactive</MenuItem>
        <MenuItem value="preventive">Preventive</MenuItem>
        <MenuItem value="inspection">Inspection</MenuItem>
        <MenuItem value="safety">Safety</MenuItem>
        <MenuItem value="other">Other</MenuItem>
      </Select>
    )
    if (field === 'category') return (
      <Select value={config.value ?? ''} onChange={(e) => setValue(e.target.value)} size="small" fullWidth displayEmpty>
        <MenuItem value="">Select value</MenuItem>
        {categories.map((c: any) => (
          <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
        ))}
      </Select>
    )
    if (field === 'source') return (
      <Select value={config.value ?? ''} onChange={(e) => setValue(e.target.value)} size="small" fullWidth displayEmpty>
        <MenuItem value="">Select value</MenuItem>
        <MenuItem value="internal">Submitted inside the app</MenuItem>
        <MenuItem value="public_qr">Public QR scan</MenuItem>
        <MenuItem value="email">Inbound email</MenuItem>
        <MenuItem value="arcos_alarm">IoT alarm (Arcos)</MenuItem>
        <MenuItem value="checklist_follow_up">Checklist follow-up</MenuItem>
      </Select>
    )
    if (field === 'site_id') return (
      <SearchableSelect
        value={config.value ?? ''}
        onChange={(id) => setValue(id)}
        options={sites}
        getValue={(s: any) => s.id}
        getLabel={formatSiteName}
        placeholder="Pick a site"
      />
    )
    if (field === 'location_id') {
      // Scoped picker: prompt for a site first so the location dropdown
      // is manageable (an org can have thousands of locations).
      return (
        <Stack spacing={1} sx={{ flex: 1 }}>
          <SearchableSelect
            value={config.value_site_scope ?? ''}
            onChange={(id) => onChange({ ...config, value_site_scope: id, value: '' })}
            options={sites}
            getValue={(s: any) => s.id}
            getLabel={formatSiteName}
            placeholder="Site scope"
          />
          {config.value_site_scope ? (
            <SearchableSelect
              value={config.value ?? ''}
              onChange={(id) => setValue(id)}
              options={locations}
              getValue={(l: any) => l.id}
              getLabel={(l: any) => l.name}
              placeholder="Pick a location"
            />
          ) : (
            <TextField size="small" disabled fullWidth placeholder="Pick a site first" />
          )}
        </Stack>
      )
    }
    if (kind === 'user') {
      return (
        <Select value={config.value ?? ''} onChange={(e) => setValue(e.target.value)} size="small" fullWidth displayEmpty>
          <MenuItem value="">Select user…</MenuItem>
          <MenuItem value="__self"><Chip label="Triggering user" size="small" sx={{ height: 22, bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 600 }} /></MenuItem>
          <MenuItem value="__requester"><Chip label="Requester" size="small" sx={{ height: 22, bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 600 }} /></MenuItem>
          <MenuItem value="__assignee"><Chip label="Current assignee" size="small" sx={{ height: 22, bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 600 }} /></MenuItem>
          <Divider />
          {users.map((u: any) => (
            <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
          ))}
        </Select>
      )
    }
    // in_list / not_in_list are comma-separated — same input shape as
    // free text; hint the format via placeholder.
    if (operator === 'in_list' || operator === 'not_in_list') {
      return (
        <TextField
          value={config.value ?? ''}
          onChange={(e) => setValue(e.target.value)}
          size="small" fullWidth
          placeholder="Comma-separated list, e.g. high, critical"
        />
      )
    }
    return (
      <TextField value={config.value ?? ''} onChange={(e) => setValue(e.target.value)} size="small" fullWidth />
    )
  }

  const fieldAvailable = isFieldAvailable(field, triggerType)
  const availHint = field ? availabilityLabel(field) : ''

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Select value={field} onChange={(e) => setField(e.target.value)} displayEmpty size="small" sx={{ minWidth: 200, flex: 1 }}>
          <MenuItem value=""><em>Select field...</em></MenuItem>
          {CONDITION_FIELD_GROUPS.flatMap((g) => [
            <ListSubheader key={g.group} sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em' }}>
              {g.group.toUpperCase()}
            </ListSubheader>,
            ...g.items.map((it) => {
              const ok = isFieldAvailable(it.key, triggerType)
              return (
                <MenuItem key={it.key} value={it.key} sx={{ pl: 2.5, opacity: ok ? 1 : 0.55 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <span>{it.label}</span>
                    {!ok && (
                      <Tooltip title={availabilityLabel(it.key)} placement="right">
                        <Chip label="not in context" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600 }} />
                      </Tooltip>
                    )}
                  </Stack>
                </MenuItem>
              )
            }),
          ])}
        </Select>
        <Select value={operator} onChange={(e) => setOp(e.target.value)} size="small" sx={{ minWidth: 180, flex: 1 }} disabled={!field}>
          {validOps.map((o) => (
            <MenuItem key={o} value={o}>{OPERATOR_LABELS[o] ?? o}</MenuItem>
          ))}
        </Select>
      </Stack>

      {field && (
        <Box sx={{
          px: 1.25, py: 0.75, borderRadius: 1, fontSize: 11, lineHeight: 1.55,
          bgcolor: (t) => fieldAvailable
            ? (t.palette.mode === 'light' ? '#F0F9FF' : 'rgba(59,130,246,0.06)')
            : (t.palette.mode === 'light' ? '#FEF3C7' : 'rgba(217,119,6,0.12)'),
          color: (t) => fieldAvailable
            ? (t.palette.mode === 'light' ? '#0369A1' : '#7DD3FC')
            : (t.palette.mode === 'light' ? '#92400E' : '#FCD34D'),
          border: '1px solid',
          borderColor: (t) => fieldAvailable
            ? (t.palette.mode === 'light' ? '#BAE6FD' : 'rgba(59,130,246,0.2)')
            : (t.palette.mode === 'light' ? '#FCD34D' : 'rgba(217,119,6,0.3)'),
        }}>
          {fieldAvailable
            ? <>ℹ️ {availHint}</>
            : <>⚠️ This field isn't populated by <strong>{TRIGGER_MAP[triggerType ?? '']?.label ?? triggerType}</strong>. {availHint}. The condition will always be false at run time — pick a different field, or change the workflow's trigger.</>}
        </Box>
      )}

      {field && (
        <Box sx={{ display: 'flex' }}>{renderValue()}</Box>
      )}
    </Stack>
  )
}

function DynamicValuePicker({ field, value, onChange, categories }: { field: string; value: string; onChange: (v: string) => void; categories: any[] }) {
  if (!field) return (
    <TextField label="Value" value="" size="small" fullWidth disabled placeholder="Select a field first" />
  )
  if (field === 'priority') return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} displayEmpty size="small" fullWidth>
      <MenuItem value="">Select value <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span></MenuItem>
      <MenuItem value="low"><Stack direction="row" alignItems="center" spacing={1}><Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#22C55E' }} /><span>Low</span></Stack></MenuItem>
      <MenuItem value="medium"><Stack direction="row" alignItems="center" spacing={1}><Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#F59E0B' }} /><span>Medium</span></Stack></MenuItem>
      <MenuItem value="high"><Stack direction="row" alignItems="center" spacing={1}><Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#F97316' }} /><span>High</span></Stack></MenuItem>
      <MenuItem value="critical"><Stack direction="row" alignItems="center" spacing={1}><Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#EF4444' }} /><span>Critical</span></Stack></MenuItem>
    </Select>
  )
  if (field === 'status') return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} displayEmpty size="small" fullWidth>
      <MenuItem value="">Select value <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span></MenuItem>
      <MenuItem value="open">Open</MenuItem>
      <MenuItem value="in_progress">In Progress</MenuItem>
      <MenuItem value="closed">Closed</MenuItem>
      <MenuItem value="resolved">Resolved</MenuItem>
    </Select>
  )
  if (field === 'work_type') return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} displayEmpty size="small" fullWidth>
      <MenuItem value="">Select value <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span></MenuItem>
      <MenuItem value="reactive">Reactive</MenuItem>
      <MenuItem value="preventive">Preventive</MenuItem>
      <MenuItem value="inspection">Inspection</MenuItem>
      <MenuItem value="safety">Safety</MenuItem>
      <MenuItem value="other">Other</MenuItem>
    </Select>
  )
  if (field === 'category') return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} displayEmpty size="small" fullWidth>
      <MenuItem value="">Select value <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span></MenuItem>
      {categories.map((c: any) => (
        <MenuItem key={c.id} value={c.name}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: c.color ?? '#94A3B8' }} />
            <span>{c.name}</span>
          </Stack>
        </MenuItem>
      ))}
    </Select>
  )
  return <TextField label="Value" required value={value} onChange={(e) => onChange(e.target.value)} size="small" fullWidth />
}

/**
 * Picker for the `check_value` on `escalation_timer` steps. Each
 * `check_field` option (status / priority / assignee) maps to a fixed
 * enum or a user dropdown — a free text field would let the user typo
 * the workflow into silently never escalating.
 */
function CheckValuePicker({ field, value, onChange, users }: {
  field: string; value: string; onChange: (v: string) => void; users: any[]
}) {
  if (field === 'status') return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} displayEmpty size="small" fullWidth>
      <MenuItem value=""><Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Select status</Typography></MenuItem>
      <MenuItem value="open">Open</MenuItem>
      <MenuItem value="in_progress">In Progress</MenuItem>
      <MenuItem value="closed">Closed</MenuItem>
      <MenuItem value="resolved">Resolved</MenuItem>
    </Select>
  )
  if (field === 'priority') return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} displayEmpty size="small" fullWidth>
      <MenuItem value=""><Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Select priority</Typography></MenuItem>
      <MenuItem value="low"><Stack direction="row" alignItems="center" spacing={1}><Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#22C55E' }} /><span>Low</span></Stack></MenuItem>
      <MenuItem value="medium"><Stack direction="row" alignItems="center" spacing={1}><Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#F59E0B' }} /><span>Medium</span></Stack></MenuItem>
      <MenuItem value="high"><Stack direction="row" alignItems="center" spacing={1}><Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#F97316' }} /><span>High</span></Stack></MenuItem>
      <MenuItem value="critical"><Stack direction="row" alignItems="center" spacing={1}><Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#EF4444' }} /><span>Critical</span></Stack></MenuItem>
    </Select>
  )
  if (field === 'assigned_to_user_id') return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={users}
      getValue={(u: any) => u.id}
      getLabel={(u: any) => u.name}
      placeholder="Pick a user"
      renderOption={(u: any) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={20} sx={{ bgcolor: '#0D9488' }} />
          <Typography sx={{ fontSize: 13 }}>{u.name}</Typography>
        </Stack>
      )}
    />
  )
  return <TextField label="Value" value={value} onChange={(e) => onChange(e.target.value)} size="small" fullWidth />
}

/**
 * Multi-select picker for the `stop_values` on `repeat_until` steps.
 * Stores the value as a comma-separated string for backwards compat
 * with the engine's split-on-`,` parser. Each `stop_field` maps to a
 * fixed enum / user list — free text would let typos break the loop.
 */
function StopValuesPicker({ field, value, onChange, users }: {
  field: string; value: string; onChange: (v: string) => void; users: any[]
}) {
  const selected = value.split(',').map(s => s.trim()).filter(Boolean)
  const set = (next: string[]) => onChange(next.join(','))

  if (field === 'status') {
    const options: { id: string; label: string }[] = [
      { id: 'open', label: 'Open' },
      { id: 'in_progress', label: 'In Progress' },
      { id: 'closed', label: 'Closed' },
      { id: 'resolved', label: 'Resolved' },
    ]
    return (
      <Select multiple value={selected} onChange={(e) => set((e.target.value as string[]))}
        displayEmpty size="small" fullWidth
        renderValue={(sel: string[]) => sel.length === 0
          ? <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Pick one or more…</Typography>
          : <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {sel.map((id) => <Chip key={id} label={options.find(o => o.id === id)?.label ?? id} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600 }} />)}
            </Stack>}>
        {options.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            <Checkbox size="small" checked={selected.includes(o.id)} sx={{ p: 0.5, mr: 1 }} />
            <Typography sx={{ fontSize: 13 }}>{o.label}</Typography>
          </MenuItem>
        ))}
      </Select>
    )
  }
  if (field === 'priority') {
    const options: { id: string; label: string; color: string }[] = [
      { id: 'low', label: 'Low', color: '#22C55E' },
      { id: 'medium', label: 'Medium', color: '#F59E0B' },
      { id: 'high', label: 'High', color: '#F97316' },
      { id: 'critical', label: 'Critical', color: '#EF4444' },
    ]
    return (
      <Select multiple value={selected} onChange={(e) => set((e.target.value as string[]))}
        displayEmpty size="small" fullWidth
        renderValue={(sel: string[]) => sel.length === 0
          ? <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Pick one or more…</Typography>
          : <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {sel.map((id) => {
                const o = options.find(o => o.id === id)
                return <Chip key={id} label={o?.label ?? id} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: `${o?.color}1F`, color: o?.color }} />
              })}
            </Stack>}>
        {options.map((o) => (
          <MenuItem key={o.id} value={o.id}>
            <Checkbox size="small" checked={selected.includes(o.id)} sx={{ p: 0.5, mr: 1 }} />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: o.color }} />
              <Typography sx={{ fontSize: 13 }}>{o.label}</Typography>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    )
  }
  if (field === 'assigned_to_user_id') {
    return (
      <SearchableSelect
        multiple
        value={selected}
        onChange={set}
        options={users}
        getValue={(u: any) => u.id}
        getLabel={(u: any) => u.name}
        placeholder="Pick one or more users…"
        renderOption={(u: any) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={20} sx={{ bgcolor: '#0D9488' }} />
            <Typography sx={{ fontSize: 13 }}>{u.name}</Typography>
          </Stack>
        )}
      />
    )
  }
  return <TextField label="Values (comma-separated)" value={value} onChange={(e) => onChange(e.target.value)} size="small" fullWidth />
}

/**
 * Simulated-context panel for the Test run tab. Lets the tester scope
 * the fabricated context to a specific site/location + priority so
 * scenario-specific rules ("only from Site X", "after hours only",
 * "priority high") actually get exercised. Blank = server-side default
 * (first accessible site + first location).
 */
function TestSimulationPanel({ siteId, onSiteChange, locationId, onLocationChange, priority, onPriorityChange }: {
  siteId: string
  onSiteChange: (id: string) => void
  locationId: string
  onLocationChange: (id: string) => void
  priority: string
  onPriorityChange: (p: string) => void
}) {
  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000 })
  const sites: any[] = Array.isArray(sitesQ.data) ? sitesQ.data : []
  const locsQ = useQuery({
    queryKey: ['locations', siteId],
    queryFn: () => api.locations.list(siteId),
    enabled: Boolean(siteId), staleTime: 60_000,
  })
  const locations: any[] = Array.isArray(locsQ.data) ? locsQ.data : []

  return (
    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1.5 }}>Simulated context</Typography>
      <Stack spacing={1.5}>
        <SearchableSelect
          value={siteId}
          onChange={(id) => { onSiteChange(id); onLocationChange('') }}
          options={sites}
          getValue={(s: any) => s.id}
          getLabel={formatSiteName}
          placeholder="Site — leave empty for first accessible"
        />
        <SearchableSelect
          value={locationId}
          onChange={onLocationChange}
          options={locations}
          getValue={(l: any) => l.id}
          getLabel={(l: any) => l.name}
          placeholder={siteId ? 'Location — leave empty for first under this site' : 'Pick a site first (or leave both empty)'}
        />
        <Select value={priority} onChange={(e) => onPriorityChange(e.target.value)} size="small" fullWidth>
          <MenuItem value="low">Low priority</MenuItem>
          <MenuItem value="medium">Medium priority</MenuItem>
          <MenuItem value="high">High priority</MenuItem>
          <MenuItem value="critical">Critical priority</MenuItem>
        </Select>
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        <HintBox tone="info">
          These override what the fake trigger stamps into context. Leave them empty and the backend fabricates a QR-like submission from your first accessible site — perfect for a smoke test. Override when your workflow filters on a specific site/location and you want to verify the filter fires.
        </HintBox>
      </Box>
    </Box>
  )
}

function BranchStepList({ label, color, steps, branchKey, options, onAdd, onRemove, onUpdate, triggerType, customerId }: {
  label: string; color: string; steps: any[]; branchKey: 'then_steps' | 'else_steps'
  options: typeof STEP_TYPES
  onAdd: (branch: 'then_steps' | 'else_steps', stepType: string) => void
  onRemove: (branch: 'then_steps' | 'else_steps', idx: number) => void
  onUpdate: (branch: 'then_steps' | 'else_steps', idx: number, updates: any) => void
  triggerType?: string
  customerId?: string
}) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)

  return (
    <Box sx={{ p: 2, borderRadius: 2, bgcolor: `${color}08`, border: '1px solid', borderColor: `${color}30` }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color, mb: 1.5 }}>{label}</Typography>
      <Stack spacing={1}>
        {steps.map((sub: any, si: number) => {
          const sm = STEP_MAP[sub.step_type]; const sc = sm?.color ?? '#64748B'
          const isExpanded = expandedIdx === si
          return (
            <Box key={si} sx={{ borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid',
              borderColor: isExpanded ? sc : 'divider', overflow: 'hidden', transition: 'border-color 120ms' }}>
              {/* Header row */}
              <Stack direction="row" alignItems="center" spacing={1}
                onClick={() => setExpandedIdx(isExpanded ? null : si)}
                sx={{ px: 1.5, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ width: 22, height: 22, borderRadius: 99, border: '2px solid', borderColor: sc,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 800, color: sc }}>{si + 1}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, flex: 1 }} noWrap>{sub.label || sm?.label || sub.step_type}</Typography>
                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>{isExpanded ? 'collapse' : 'edit'}</Typography>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(branchKey, si) }}
                  sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' }, p: 0.25 }}>
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Stack>

              {/* Expanded config */}
              {isExpanded && (
                <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <TextField label="Step label" value={sub.label ?? ''} onChange={(e) => onUpdate(branchKey, si, { label: e.target.value })}
                    size="small" fullWidth sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { fontSize: 12.5 } }} />
                  <StepConfigFields stepType={sub.step_type} config={sub.config ?? {}}
                    onChange={(newConfig) => onUpdate(branchKey, si, { config: newConfig })}
                    triggerType={triggerType} customerId={customerId} />
                </Box>
              )}
            </Box>
          )
        })}

        <Select value="" onChange={(e) => { if (e.target.value) onAdd(branchKey, e.target.value) }} displayEmpty size="small" fullWidth
          sx={{ '& .MuiOutlinedInput-input': { fontSize: 12.5 } }}>
          <MenuItem value="" sx={{ fontSize: 12.5, color: 'text.secondary' }}>+ Add step to {branchKey === 'then_steps' ? 'THEN' : 'ELSE'} path</MenuItem>
          {options.map((s) => (
            <MenuItem key={s.key} value={s.key} sx={{ fontSize: 12.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: s.color, flexShrink: 0 }} />
                <span>{s.label}</span>
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </Box>
  )
}

function ChecklistConditionSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const clQ = useQuery({ queryKey: ['checklists', { status: 'published' }], queryFn: () => api.checklists.list({ status: 'published' }), staleTime: 60_000 })
  const checklists: any[] = Array.isArray(clQ.data) ? clQ.data : []

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={checklists}
      getValue={(c) => c.id}
      getLabel={(c) => c.name}
      placeholder="Any checklist"
      renderOption={(c) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <ChecklistOutlinedIcon sx={{ fontSize: 16, color: '#059669' }} />
          <Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
        </Stack>
      )}
    />
  )
}

// Trigger-condition picker for `asset.soft_point_reading` — picks a
// specific asset, then one of ITS soft points (soft points have no
// cross-asset catalog/identity today, only a per-asset name, so a
// workflow always targets one exact asset_id + soft_point_id pair
// rather than "any asset with a point named X"). Writes both keys at
// once via `onChange`, unlike every other trigger-condition type here
// which only ever writes its own single `c.key`.
function AssetSoftPointPicker({ assetId, softPointId, onChange }: {
  assetId: string
  softPointId: string
  onChange: (patch: { asset_id?: string; soft_point_id?: string }) => void
}) {
  const assetsQ = useQuery({ queryKey: ['assets'], queryFn: () => api.assets.list({}), staleTime: 60_000 })
  const assets: any[] = Array.isArray(assetsQ.data) ? assetsQ.data : []

  const pointsQ = useQuery({
    queryKey: ['asset-soft-points', assetId],
    queryFn: () => api.assets.listSoftPoints(assetId),
    enabled: Boolean(assetId),
    staleTime: 60_000,
  })
  const points: any[] = Array.isArray(pointsQ.data) ? pointsQ.data : []

  return (
    <Stack spacing={1}>
      <SearchableSelect
        value={assetId}
        // Changing the asset invalidates whatever soft point was picked
        // for the old one — same cascade-clear pattern site_id → location_id
        // uses elsewhere in this file.
        onChange={(id) => onChange({ asset_id: id, soft_point_id: '' })}
        options={assets}
        getValue={(a: any) => a.id}
        getLabel={(a: any) => `${a.name} (${a.asset_code})`}
        placeholder="Pick an asset"
      />
      {!assetId ? (
        <TextField size="small" disabled fullWidth placeholder="Pick an asset first" />
      ) : pointsQ.isLoading ? (
        <TextField size="small" disabled fullWidth placeholder="Loading soft points…" />
      ) : points.length === 0 ? (
        <Typography sx={{ fontSize: 11.5, color: 'text.disabled', px: 0.5 }}>
          This asset has no soft points yet — add one from its Telemetry tab first.
        </Typography>
      ) : (
        <SearchableSelect
          value={softPointId}
          onChange={(id) => onChange({ soft_point_id: id })}
          options={points}
          getValue={(p: any) => p.id}
          getLabel={(p: any) => `${p.name}${p.unit ? ` (${p.unit})` : ''}`}
          placeholder="Pick a soft point"
        />
      )}
    </Stack>
  )
}

function SortableStepCard({ id, children }: { id: string; children: (listeners: Record<string, any>, isDragging: boolean) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <Box ref={setNodeRef} {...attributes}
      sx={{ transform: CSS.Transform.toString(transform), transition: transition ?? 'transform 200ms ease',
        opacity: isDragging ? 0.45 : 1, position: 'relative', zIndex: isDragging ? 10 : 'auto',
        width: '100%' }}>
      {children(listeners ?? {}, isDragging)}
    </Box>
  )
}

function FlowLine() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: 32 }}>
      <Box sx={{ width: 2, flex: 1, bgcolor: (t) => t.palette.mode === 'light' ? '#CBD5E1' : 'rgba(255,255,255,0.12)' }} />
    </Box>
  )
}

// ─── Run history table ──────────────────────────────────────────────────────

function RunTable({ runs, formatDate }: { runs: any[]; formatDate: (d: string) => string }) {
  const STATUS_C: Record<string, string> = { completed: '#059669', failed: '#DC2626', running: '#D97706', cancelled: '#64748B' }
  return (
    <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Stack direction="row" sx={{ px: 2.5, py: 1, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
        <Typography sx={{ flex: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Run</Typography>
        <Typography sx={{ flex: 2, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Started</Typography>
        <Typography sx={{ flex: 1, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Duration</Typography>
        <Typography sx={{ flex: 1.5, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Status</Typography>
      </Stack>
      {runs.map((r: any) => {
        const dur = r.completed_at ? fmtDur(new Date(r.completed_at).getTime() - new Date(r.started_at).getTime()) : '—'
        const c = STATUS_C[r.status] ?? '#64748B'
        return (
          <Stack key={r.id} direction="row" alignItems="center" sx={{ px: 2.5, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ flex: 1.5 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, fontFamily: 'ui-monospace, monospace' }}>R-{r.id.slice(0, 4).toUpperCase()}</Typography>
              {r.trigger_ref && <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>WO-{String(r.trigger_ref).slice(0, 6).toUpperCase()}</Typography>}
            </Box>
            <Typography sx={{ flex: 2, fontSize: 12.5, color: 'text.secondary' }}>{formatDate(r.started_at)}</Typography>
            <Typography sx={{ flex: 1, fontSize: 12.5, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{dur}</Typography>
            <Box sx={{ flex: 1.5 }}>
              <Chip label={r.status} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: `${c}15`, color: c }} />
              {r.error && <Typography sx={{ fontSize: 10, color: '#DC2626', mt: 0.25 }} noWrap>{r.error}</Typography>}
            </Box>
          </Stack>
        )
      })}
    </Box>
  )
}

function fmtDur(ms: number) {
  const s = Math.floor(ms / 1000); if (s < 60) return `${s}s`
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ${s % 60}s`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}

// ─── Layout helpers ─────────────────────────────────────────────────────────

function Card({ title, extra, children }: { title: string; extra?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{title}</Typography>
        {extra}
      </Stack>
      <Stack spacing={0} divider={<Divider />} sx={{ px: 3 }}>
        {children}
      </Stack>
    </Box>
  )
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ py: 1.75 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', width: 110, flexShrink: 0, pt: 0.25 }}>{label}</Typography>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Stack>
  )
}

function fmtConditions(config: Record<string, any> | null): string {
  if (!config) return ''
  const parts = Object.entries(config).filter(([k, v]) => v && k !== 'frequency' && k !== 'hour' && k !== 'minute' && k !== 'day_of_week' && k !== 'day_of_month' && k !== 'month' && k !== 'interval' && k !== 'interval_unit' && k !== 'days').map(([k, v]) => `${k}=${v}`)
  return parts.length > 0 ? ` · ${parts.join(', ')}` : ''
}

function stepHint(s: WorkflowStep): string {
  const c = s.config ?? {}
  if (s.step_type === 'approval' && c.approver_name) return `Requires ${c.approver_name}`
  if (s.step_type === 'notification' && c.notify_name) return `Notify ${c.notify_name}`
  if (s.step_type === 'email' && c.to) return `To ${c.to}`
  if (s.step_type === 'assign' && c.assignee_name) return c.assignee_name
  if (s.step_type === 'condition' && c.field) return `If ${c.field} ${c.operator ?? '='} ${c.value}`
  if (s.step_type === 'delay') return `Wait ${c.duration} ${c.unit ?? 'hours'}`
  if (s.step_type === 'create_work_order' && c.title) return c.title
  if (s.step_type === 'update_field' && c.field) return `${c.field} = ${c.value}`
  if (s.step_type === 'attach_checklist' && c.template_name) return c.template_name
  if (s.step_type === 'webhook' && c.url) return c.url.replace(/^https?:\/\//, '').slice(0, 40)
  if (s.step_type === 'if_else' && c.field) return `If ${c.field} ${c.operator ?? '='} ${c.value}`
  if (s.step_type === 'escalation_timer') return `Wait ${c.wait_duration} ${c.wait_unit ?? 'hours'}, then escalate`
  if (s.step_type === 'repeat_until') return `Every ${c.interval_duration} ${c.interval_unit ?? 'hours'} (max ${c.max_iterations ?? 3}x)`
  return ''
}

// ─── Dialogs (Add step, Edit step, Trigger edit) ────────────────────────────

function AddStepDialog({ onClose, onAdd, pending }: { onClose: () => void; onAdd: (b: any) => void; pending: boolean }) {
  const [stepType, setStepType] = useState('')
  const [label, setLabel] = useState('')
  const [config, setConfig] = useState<Record<string, any>>({})

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 0.5 }}>Add a step</DialogTitle>
      <Typography sx={{ px: 3, fontSize: 13, color: 'text.secondary', mb: 1 }}>Pick a step type to insert.</Typography>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Step type grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
            {STEP_TYPES.map((s) => (
              <Box key={s.key} onClick={() => { setStepType(s.key); setLabel(s.label); setConfig({}) }}
                sx={{ px: 2, py: 1.5, borderRadius: 2, cursor: 'pointer', border: '1px solid',
                  borderColor: stepType === s.key ? s.color : 'divider',
                  bgcolor: stepType === s.key ? `${s.color}08` : 'transparent',
                  transition: 'all 100ms', '&:hover': { borderColor: s.color } }}>
                <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: `${s.color}14`, color: s.color, mt: 0.25 }}><s.icon sx={{ fontSize: 15 }} /></Avatar>
                  <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{s.label}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1.3 }}>{s.description}</Typography>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Box>

          {stepType && (
            <>
              {STEP_HELP[stepType] && (
                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: (t) => t.palette.mode === 'light' ? '#F0F9FF' : 'rgba(59,130,246,0.06)',
                  border: '1px solid', borderColor: (t) => t.palette.mode === 'light' ? '#BAE6FD' : 'rgba(59,130,246,0.2)' }}>
                  <Typography sx={{ fontSize: 11.5, color: (t) => t.palette.mode === 'light' ? '#0369A1' : '#7DD3FC', lineHeight: 1.5 }}>💡 {STEP_HELP[stepType]}</Typography>
                </Box>
              )}
              <TextField label="Step label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} size="small" fullWidth placeholder="e.g. Manager approval" />
              <StepConfigFields stepType={stepType} config={config} onChange={setConfig} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!stepType || pending} onClick={() => onAdd({ step_type: stepType, label, config })} sx={{ borderRadius: 2, fontWeight: 600 }}>{pending ? 'Adding...' : 'Add step'}</Button>
      </DialogActions>
    </Dialog>
  )
}

function EditStepDialog({ step, onClose, onSave, pending }: { step: WorkflowStep; onClose: () => void; onSave: (b: any) => void; pending: boolean }) {
  const [label, setLabel] = useState(step.label ?? '')
  const [config, setConfig] = useState<Record<string, any>>(step.config ?? {})
  const m = STEP_MAP[step.step_type]
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Edit: {m?.label ?? step.step_type}</DialogTitle>
      <DialogContent><Stack spacing={2.5} sx={{ mt: 1 }}>
        <TextField label="Label" value={label} onChange={(e) => setLabel(e.target.value)} size="small" fullWidth />
        <StepConfigFields stepType={step.step_type} config={config} onChange={setConfig} />
      </Stack></DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={pending} onClick={() => onSave({ label, config })} sx={{ borderRadius: 2, fontWeight: 600 }}>{pending ? 'Saving...' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  )
}

function TriggerEditDialog({ triggerType, triggerConfig, onSave, onClose }: {
  triggerType: string; triggerConfig: Record<string, any>; onSave: (t: string, c: Record<string, any>) => void; onClose: () => void
}) {
  const [sel, setSel] = useState(triggerType)
  const [cfg, setCfg] = useState<Record<string, any>>(triggerConfig)
  const conditions = TRIGGER_CONDITIONS[sel] ?? []

  // Site / location pickers for filter conditions. Locations only load
  // once a site is picked — matches the /sites/:id/locations API shape.
  const needsSitePicker = conditions.some((c) => c.type === 'site' || c.type === 'location')
  const sitesQ = useQuery({
    queryKey: ['sites'], queryFn: () => api.sites.list(),
    staleTime: 5 * 60_000, enabled: needsSitePicker,
  })
  const sites: any[] = Array.isArray(sitesQ.data) ? sitesQ.data : []
  const filterSite = cfg.site_id
  const locationsQ = useQuery({
    queryKey: ['locations', filterSite],
    queryFn: () => api.locations.list(filterSite),
    enabled: Boolean(filterSite && conditions.some((c) => c.type === 'location')),
    staleTime: 60_000,
  })
  const locations: any[] = Array.isArray(locationsQ.data) ? locationsQ.data : []

  const setKey = (k: string, v: any) => {
    const n = { ...cfg }
    if (v === '' || v === null || v === undefined) delete n[k]
    else n[k] = v
    // Clearing site_id invalidates a stale location_id filter — silently
    // drop it so we don't leave a dangling condition the user can't see.
    if (k === 'site_id' && !v) delete n.location_id
    setCfg(n)
  }

  const timeWindow = (cfg.time_window && typeof cfg.time_window === 'object')
    ? cfg.time_window : { start: '', end: '', tz: '' }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Configure trigger</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>Trigger event</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.75 }}>
              {TRIGGER_TYPES.map((t) => (
                <Box key={t.key} onClick={() => { setSel(t.key); if (t.key !== triggerType) setCfg({}) }}
                  sx={{ px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer', border: '1px solid',
                    borderColor: sel === t.key ? t.color : 'divider', bgcolor: sel === t.key ? `${t.color}08` : 'transparent',
                    transition: 'all 100ms', '&:hover': { borderColor: t.color } }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{t.label}</Typography>
                  <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{t.category}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          {sel === 'schedule' ? (
            <Box><Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.5 }}>Schedule</Typography><ScheduleConfig config={cfg} onChange={setCfg} /></Box>
          ) : conditions.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>Filter conditions</Typography>
              <Stack spacing={1.5}>
                {conditions.map((c) => (
                  <Box key={c.key}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>{c.label}</Typography>
                    {c.type === 'select' ? (
                      <Select value={cfg[c.key] ?? ''} onChange={(e) => setKey(c.key, e.target.value)} size="small" fullWidth displayEmpty>
                        {(c.options ?? []).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    ) : c.type === 'site' ? (
                      <SearchableSelect
                        value={cfg[c.key] ?? ''}
                        onChange={(id) => setKey(c.key, id)}
                        options={sites}
                        getValue={(s: any) => s.id}
                        getLabel={formatSiteName}
                        placeholder="Any site"
                      />
                    ) : c.type === 'location' ? (
                      <SearchableSelect
                        value={cfg[c.key] ?? ''}
                        onChange={(id) => setKey(c.key, id)}
                        options={locations}
                        getValue={(l: any) => l.id}
                        getLabel={(l: any) => l.name}
                        placeholder={filterSite ? 'Any location' : 'Pick a site first'}
                      />
                    ) : c.type === 'time_window' ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          type="time" size="small" label="From"
                          value={timeWindow.start ?? ''}
                          onChange={(e) => setKey(c.key, { ...timeWindow, start: e.target.value })}
                          InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                        />
                        <TextField
                          type="time" size="small" label="To"
                          value={timeWindow.end ?? ''}
                          onChange={(e) => setKey(c.key, { ...timeWindow, end: e.target.value })}
                          InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small" label="Timezone"
                          value={timeWindow.tz ?? ''}
                          onChange={(e) => setKey(c.key, { ...timeWindow, tz: e.target.value })}
                          placeholder="Asia/Kolkata" sx={{ flex: 1.2 }}
                        />
                      </Stack>
                    ) : c.type === 'asset_soft_point' ? (
                      <AssetSoftPointPicker
                        assetId={cfg.asset_id ?? ''}
                        softPointId={cfg.soft_point_id ?? ''}
                        onChange={(patch) => setCfg((prev) => ({ ...prev, ...patch }))}
                      />
                    ) : <TextField value={cfg[c.key] ?? ''} onChange={(e) => setKey(c.key, e.target.value)} size="small" fullWidth />}
                    {c.helper && (
                      <Typography sx={{ fontSize: 10.5, color: 'text.secondary', mt: 0.5 }}>{c.helper}</Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          {TRIGGER_HELP[sel] && (
            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: (t) => t.palette.mode === 'light' ? '#F0F9FF' : 'rgba(59,130,246,0.06)', border: '1px solid', borderColor: (t) => t.palette.mode === 'light' ? '#BAE6FD' : 'rgba(59,130,246,0.2)' }}>
              <Typography sx={{ fontSize: 11.5, color: (t) => t.palette.mode === 'light' ? '#0369A1' : '#7DD3FC', lineHeight: 1.5 }}>💡 {TRIGGER_HELP[sel]}</Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(sel, cfg)} sx={{ borderRadius: 2, fontWeight: 600 }}>Save trigger</Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Step config fields ─────────────────────────────────────────────────────

// ─── Shared UX helpers ─────────────────────────────────────────────────────

/**
 * Small callout box used across step configs. Communicates prerequisites,
 * behavioural surprises, or usage tips so the user isn't left guessing.
 * `tone` picks the colour palette; content is any node.
 */
function HintBox({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'danger' | 'ok'; children: React.ReactNode }) {
  const palette = {
    info:   { fg: '#0369A1', bg: '#F0F9FF', border: '#BAE6FD', fgDark: '#7DD3FC', bgDark: 'rgba(59,130,246,0.06)',  borderDark: 'rgba(59,130,246,0.2)' },
    warn:   { fg: '#92400E', bg: '#FEF3C7', border: '#FCD34D', fgDark: '#FCD34D', bgDark: 'rgba(217,119,6,0.12)',  borderDark: 'rgba(217,119,6,0.3)' },
    danger: { fg: '#991B1B', bg: '#FEE2E2', border: '#FCA5A5', fgDark: '#FCA5A5', bgDark: 'rgba(220,38,38,0.12)',  borderDark: 'rgba(220,38,38,0.3)' },
    ok:     { fg: '#166534', bg: '#F0FDF4', border: '#BBF7D0', fgDark: '#86EFAC', bgDark: 'rgba(16,185,129,0.06)', borderDark: 'rgba(16,185,129,0.2)' },
  } as const
  const p = palette[tone]
  return (
    <Box sx={{
      px: 1.25, py: 0.75, borderRadius: 1, fontSize: 11.5, lineHeight: 1.55,
      bgcolor: (t) => t.palette.mode === 'light' ? p.bg : p.bgDark,
      color: (t) => t.palette.mode === 'light' ? p.fg : p.fgDark,
      border: '1px solid',
      borderColor: (t) => t.palette.mode === 'light' ? p.border : p.borderDark,
    }}>
      {children}
    </Box>
  )
}

/**
 * Radio toggle for the plain / markdown format field on Email +
 * Notification steps. Kept as a shared component so both step configs
 * stay visually consistent. Backwards-compatible: undefined format
 * behaves like 'plain'.
 */
function FormatToggle({ value, onChange }: {
  value: 'plain' | 'markdown' | undefined
  onChange: (v: 'plain' | 'markdown') => void
}) {
  const current = value === 'markdown' ? 'markdown' : 'plain'
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Format</Typography>
      <Stack direction="row" spacing={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
        {[
          { key: 'plain',    label: 'Plain text', hint: 'Line breaks preserved, no formatting.' },
          { key: 'markdown', label: 'Markdown',   hint: '**bold**, *italic*, `code`, [links](https://…), bullet lists.' },
        ].map((f, i) => (
          <Box key={f.key}
            onClick={() => onChange(f.key as 'plain' | 'markdown')}
            sx={{ flex: 1, py: 1, px: 1.25, textAlign: 'center', cursor: 'pointer',
              bgcolor: current === f.key ? (t) => `${t.palette.primary.main}12` : 'transparent',
              borderRight: i === 0 ? '1px solid' : 'none', borderColor: 'divider',
              transition: 'all 100ms', '&:hover': { bgcolor: 'action.hover' } }}>
            <Typography sx={{ fontSize: 13, fontWeight: current === f.key ? 700 : 500 }}>{f.label}</Typography>
            <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.25, lineHeight: 1.4 }}>{f.hint}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

/**
 * Live preview of a rendered message body. Runs the same
 * escape → optional-markdown pipeline as the backend renderer, then
 * substitutes preview values for {{tokens}} so admins can see the real
 * shape of the message before saving. Unknown tokens render as literal
 * `{{typo}}` markers so misspelled tokens jump out of the preview.
 */
function MessagePreview({ body, subject, format, tone = 'email' }: {
  body: string
  subject?: string
  format?: 'plain' | 'markdown'
  tone?: 'email' | 'notification'
}) {
  // Order matters: interpolate first (turns {{title}} into the sample
  // value), then feed to the renderer which handles escaping. This is
  // the same order the backend runs.
  const withTokens = interpolatePreview(body || '')
  const html = renderMessageBody(withTokens, format)
  const subjWithTokens = interpolatePreview(subject || '')
  const headerBg = tone === 'notification' ? '#7C3AED' : '#0D9488'
  const headerText = tone === 'notification' ? 'Workflow Notification' : (subjWithTokens || 'Automated notification')
  return (
    <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', bgcolor: '#f4f5f7', p: 2 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.disabled', mb: 1 }}>
        Preview (with sample values)
      </Typography>
      <Box sx={{ maxWidth: 480, mx: 'auto', bgcolor: '#fff', borderRadius: 1.5, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <Box sx={{ bgcolor: headerBg, color: '#fff', px: 2.5, py: 1.5, fontSize: 14, fontWeight: 700 }}>{headerText}</Box>
        <Box sx={{ px: 2.5, py: 2 }}>
          {tone === 'notification' && <Typography sx={{ fontSize: 13, color: '#1e293b', mb: 1 }}>Hi [Daniel],</Typography>}
          <Box sx={{ fontSize: 13, color: '#475569', lineHeight: 1.65,
            '& p': { margin: '0 0 12px 0' },
            '& ul, & ol': { margin: '0 0 12px 0', paddingLeft: '22px' },
            '& code': { background: '#f1f5f9', padding: '1px 5px', borderRadius: '3px', fontFamily: 'ui-monospace,Menlo,monospace', fontSize: '0.92em' },
            '& a': { color: '#0D9488', textDecoration: 'underline' },
          }} dangerouslySetInnerHTML={{ __html: html || '<span style="color:#94a3b8;font-style:italic">Empty message</span>' }} />
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Trigger dependencies per step type. Keyed by step_type. Empty entry =
 * step works with any trigger (e.g. Email, Webhook, Delay). Used to
 * surface a warning when the workflow's trigger doesn't provide the
 * context this step needs.
 */
const STEP_TRIGGER_REQUIREMENTS: Record<string, {
  needs: string
  matches: (triggerType: string) => boolean
  usage: string
}> = {
  assign: {
    needs: 'a work order in context',
    matches: (t) => t.startsWith('work_order.'),
    usage: 'Assigns whoever/whichever team you pick to the current work order. Only meaningful on work_order.* triggers, or when a Create work order step ran earlier in this workflow.',
  },
  attach_checklist: {
    needs: 'a work order in context',
    matches: (t) => t.startsWith('work_order.'),
    usage: 'Attaches the picked checklist template to the current work order. Same trigger rules as Assign.',
  },
  approval: {
    needs: 'a work order in context',
    matches: (t) => t.startsWith('work_order.'),
    usage: 'Pauses the workflow until the picked approver acts. Approval rows are written against the work order, so a WO must be in context.',
  },
  update_field: {
    needs: 'a resource in context (work order, asset, or part)',
    matches: (t) => t.startsWith('work_order.') || t.startsWith('asset.') || t.startsWith('part.'),
    usage: 'Updates a single field on the resource the trigger fired on. If the trigger doesn\'t identify one resource, this step is a silent no-op.',
  },
  update_request_status: {
    needs: 'a request in context',
    matches: (t) => t === 'request.submitted',
    usage: 'Approves or declines the source maintenance request. Only meaningful when the trigger is Request submitted.',
  },
  copy_request_attachments: {
    needs: 'a request in context and a work order in context',
    matches: (t) => t === 'request.submitted',
    usage: 'Copies every photo from the source request onto the current work order. Usually placed after a Create work order step in a request.submitted workflow.',
  },
  escalation_timer: {
    needs: 'a work order in context',
    matches: (t) => t.startsWith('work_order.'),
    usage: 'Re-reads the work order after a wait window and runs escalation steps if the condition is still unmet. Only meaningful for WO-shaped workflows.',
  },
  repeat_until: {
    needs: 'a work order in context',
    matches: (t) => t.startsWith('work_order.'),
    usage: 'Repeats a set of actions on an interval until a stop condition on the WO is met. Requires a WO in context.',
  },
  // Steps that work anywhere — listed for completeness so the sweep is
  // exhaustive and future-you can spot missing entries at a glance.
  notification:   { needs: 'any trigger', matches: () => true, usage: '' },
  email:          { needs: 'any trigger', matches: () => true, usage: '' },
  webhook:        { needs: 'any trigger', matches: () => true, usage: '' },
  delay:          { needs: 'any trigger', matches: () => true, usage: '' },
  condition:      { needs: 'any trigger', matches: () => true, usage: '' },
  if_else:        { needs: 'any trigger', matches: () => true, usage: '' },
  switch:         { needs: 'any trigger', matches: () => true, usage: '' },
  create_work_order: { needs: 'any trigger', matches: () => true, usage: '' },
  create_task:       { needs: 'any trigger', matches: () => true, usage: '' },
}

/**
 * Parse duration+unit into milliseconds. Mirrors the engine so warnings
 * stay accurate when the engine's cap is updated.
 */
function durationMs(duration: any, unit: string): number {
  const n = parseInt(String(duration), 10)
  if (!Number.isFinite(n) || n <= 0) return 0
  if (unit === 'minutes') return n * 60_000
  if (unit === 'hours')   return n * 3_600_000
  if (unit === 'days')    return n * 86_400_000
  return n * 60_000
}
const INLINE_WAIT_CAP_MS = 300_000 // 5 min — mirrors engine.js caps

function StepConfigFields({ stepType, config, onChange, workflowId, stepId, triggerType, customerId }: { stepType: string; config: Record<string, any>; onChange: (c: Record<string, any>) => void; workflowId?: string; stepId?: string; triggerType?: string; customerId?: string }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  // Scoped to the workflow's own customer (when it's pinned to one) so
  // the approver/assignee/escalate/notify pickers below can't offer a
  // user who'd have no access to the resulting work order — see the
  // "Test" task incident where an approver outside the customer's
  // user_customers grants got picked and could never see what they
  // were assigned to approve.
  const usersQ = useQuery({
    queryKey: ['users', { customer_id: customerId }],
    queryFn: () => api.users.list(customerId ? { customer_id: customerId } : undefined),
    staleTime: 60_000,
  })
  const users = Array.isArray(usersQ.data) ? usersQ.data : []

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000, enabled: stepType === 'create_work_order' || stepType === 'create_task' || stepType === 'switch' || stepType === 'condition' || stepType === 'if_else' })
  const sites: any[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  // Locations for a condition-value picker whose field is `location_id`.
  // Site scope for the picker is stored under `value_site_scope` so the
  // value picker knows which site's locations to load — the run-time
  // engine doesn't need it, only the builder.
  const conditionSiteScope = (stepType === 'condition' || stepType === 'if_else')
    ? (config.value_site_scope ?? '') : ''
  const conditionLocationsQ = useQuery({
    queryKey: ['locations', conditionSiteScope],
    queryFn: () => api.locations.list(conditionSiteScope),
    enabled: Boolean(conditionSiteScope),
    staleTime: 60_000,
  })
  const conditionLocations: any[] = Array.isArray(conditionLocationsQ.data) ? conditionLocationsQ.data : []

  // Locations for the switch step. A switch on `location_id` needs a
  // site to scope the case picker — we store that site under `site_scope`
  // in the step config so it's persisted and reloaded correctly.
  const switchLocationSiteId = stepType === 'switch' && config.field === 'location_id' ? (config.site_scope ?? '') : ''
  const switchLocationsQ = useQuery({
    queryKey: ['locations', switchLocationSiteId],
    queryFn: () => api.locations.list(switchLocationSiteId),
    enabled: Boolean(switchLocationSiteId),
    staleTime: 60_000,
  })
  const switchLocations: any[] = Array.isArray(switchLocationsQ.data) ? switchLocationsQ.data : []

  // Locations for the create_work_order step's explicit Location picker.
  // Enabled once a site is chosen; changing the site clears the location
  // so we never store a dangling id from a previous site.
  const createWoSiteId = (stepType === 'create_work_order' || stepType === 'create_task') ? (config.site_id ?? '') : ''
  const createWoLocationsQ = useQuery({
    queryKey: ['locations', createWoSiteId],
    queryFn: () => api.locations.list(createWoSiteId),
    enabled: Boolean(createWoSiteId),
    staleTime: 60_000,
  })
  const createWoLocations: any[] = Array.isArray(createWoLocationsQ.data) ? createWoLocationsQ.data : []

  const assetsQ = useQuery({ queryKey: ['assets'], queryFn: () => api.assets.list({}), staleTime: 60_000, enabled: stepType === 'create_work_order' })
  const assets: any[] = Array.isArray(assetsQ.data) ? assetsQ.data : []
  const filteredAssets = config.site_id ? assets.filter((a) => a.site_id === config.site_id) : assets

  const teamsQ = useQuery({ queryKey: ['teams'], queryFn: () => api.teams.list(), staleTime: 60_000, enabled: stepType === 'create_work_order' || stepType === 'create_task' || stepType === 'assign' })
  const teams: any[] = Array.isArray(teamsQ.data) ? teamsQ.data : []

  const vendorsQ = useQuery({ queryKey: ['vendors'], queryFn: () => api.vendors.list(), staleTime: 60_000, enabled: stepType === 'create_work_order' })
  const vendors: any[] = Array.isArray(vendorsQ.data) ? vendorsQ.data : []

  const needsCategories = ['create_work_order', 'create_task', 'update_field', 'condition', 'if_else'].includes(stepType)
  const categoriesQ = useQuery({ queryKey: ['asset-categories'], queryFn: () => api.assets.listCategories(), staleTime: 60_000, enabled: needsCategories })
  const categories: any[] = Array.isArray(categoriesQ.data) ? categoriesQ.data : []

  const checklistsQ = useQuery({ queryKey: ['checklists', { status: 'published' }], queryFn: () => api.checklists.list({ status: 'published' }), staleTime: 60_000, enabled: stepType === 'create_work_order' || stepType === 'create_task' || stepType === 'attach_checklist' })
  const checklists: any[] = Array.isArray(checklistsQ.data) ? checklistsQ.data : []

  const reportTemplatesQ = useQuery({ queryKey: ['report-templates-summary'], queryFn: () => api.reportTemplates.listSummaries(), staleTime: 60_000, enabled: stepType === 'create_work_order' || stepType === 'create_task' })
  const reportTemplates: any[] = Array.isArray(reportTemplatesQ.data) ? reportTemplatesQ.data : []

  const iconSx = { fontSize: 16, color: 'text.secondary', mr: 1 }
  const PRIORITY_COLORS: Record<string, string> = { low: '#22C55E', medium: '#F59E0B', high: '#F97316', critical: '#EF4444' }

  // Trigger-context requirement banner: warns when this step won't do
  // anything at run time because the workflow's trigger doesn't provide
  // the needed context. Also shows a plain informational usage note
  // when the step is fine but its behaviour is non-obvious.
  const stepReq = STEP_TRIGGER_REQUIREMENTS[stepType]
  const reqUnmet = Boolean(triggerType && stepReq && !stepReq.matches(triggerType))
  const banner = reqUnmet ? (
    <HintBox tone="warn">
      <strong>⚠️ Won't run on this trigger.</strong> This step needs {stepReq?.needs}, but the workflow's trigger is <strong>{TRIGGER_MAP[triggerType ?? '']?.label ?? triggerType}</strong>. {stepReq?.usage} At run time it will be skipped silently — either change the trigger, place this step after a Create work order step that produces one, or remove this step.
    </HintBox>
  ) : (stepReq?.usage ? (
    <HintBox tone="info"><strong>How this step works:</strong> {stepReq.usage}</HintBox>
  ) : null)

  const stepContent = (() => {
    switch (stepType) {
    case 'approval':
      return (<Stack spacing={3}>
        {/* Approver */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Approver <Typography component="span" sx={{ color: 'error.main' }}>*</Typography></Typography>
          <SearchableSelect
            value={config.approver_id ?? ''}
            onChange={(uid) => { const u = users.find((u: any) => u.id === uid) as any; onChange({ ...config, approver_id: uid, approver_name: u?.name }) }}
            options={users}
            getValue={(u: any) => u.id}
            getLabel={(u: any) => u.name}
            placeholder="Approver"
            renderOption={(u: any) => (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={28} sx={{ bgcolor: '#0D9488' }} />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{u.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{u.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                </Box>
              </Stack>
            )}
          />
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>Person who must approve before the flow continues.</Typography>
        </Box>

        {/* Approval method */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Approval method</Typography>
          <Stack direction="row" spacing={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {[
              { key: 'push', label: 'Push', icon: NotificationsOutlinedIcon },
              { key: 'email', label: 'Email', icon: EmailOutlinedIcon },
              { key: 'in_app', label: 'In-app', icon: DesktopWindowsOutlinedIcon },
            ].map((m) => (
              <Box key={m.key} onClick={() => set('approval_method', m.key)}
                sx={{ flex: 1, py: 1.5, textAlign: 'center', cursor: 'pointer',
                  bgcolor: config.approval_method === m.key ? (t) => `${t.palette.primary.main}08` : 'transparent',
                  borderRight: m.key !== 'in_app' ? '1px solid' : 'none', borderColor: 'divider',
                  transition: 'all 100ms', '&:hover': { bgcolor: 'action.hover' } }}>
                <Typography sx={{ fontSize: 13, fontWeight: config.approval_method === m.key ? 700 : 500 }}>{m.label}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* SLA */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>SLA (hours)</Typography>
          <TextField value={config.sla_hours ?? '24'} onChange={(e) => set('sla_hours', e.target.value)} size="medium" fullWidth type="number" />
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>Auto-escalate if no response within this window.</Typography>
        </Box>

        {/* Escalation */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>If no response</Typography>
          <SearchableSelect
            value={config.escalate_to ?? ''}
            onChange={(uid) => { const u = users.find((u: any) => u.id === uid) as any; set('escalate_to', uid); set('escalate_to_name', u?.name) }}
            options={users}
            getValue={(u: any) => u.id}
            getLabel={(u: any) => u.name}
            placeholder="No escalation"
            renderOption={(u: any) => (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={28} sx={{ bgcolor: '#0D9488' }} />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{u.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{u.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                </Box>
              </Stack>
            )}
          />
        </Box>

        {/* Require comment */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Require comment</Typography>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Switch size="small" checked={config.require_comment ?? false} onChange={(e) => set('require_comment', e.target.checked)} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Approver must enter a reason</Typography>
          </Stack>
        </Box>
        <HintBox tone="info">
          <strong>How approvals block the workflow:</strong> the step writes an approval row to the WO and either notifies the approver in-app (Push / In-app) or by email. The workflow does NOT block execution here — subsequent steps run immediately. To gate further actions on the approval, put them AFTER the approval step and check the approval outcome via an If/Else on the WO status.
        </HintBox>
        <HintBox tone="info">
          <strong>SLA countdown:</strong> if the SLA window expires without a decision, the scheduler tick auto-escalates to the "If no response" user — reassigns the approval and notifies them. Leave "If no response" blank to disable auto-escalation.
        </HintBox>
      </Stack>)
    case 'notification':
      return (<Stack spacing={3}>
        {/* Channel selector */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Channel <Typography component="span" sx={{ color: 'error.main' }}>*</Typography></Typography>
          <Stack direction="row" spacing={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {[
              { key: 'push', label: 'Push', Icon: NotificationsOutlinedIcon },
              { key: 'email', label: 'Email', Icon: EmailOutlinedIcon },
              { key: 'in_app', label: 'In-app', Icon: DesktopWindowsOutlinedIcon },
            ].map((ch, i) => (
              <Box key={ch.key} onClick={() => set('channel', ch.key)}
                sx={{ flex: 1, py: 2, textAlign: 'center', cursor: 'pointer',
                  bgcolor: config.channel === ch.key ? (t) => `${t.palette.primary.main}08` : 'transparent',
                  borderRight: i < 2 ? '1px solid' : 'none', borderColor: 'divider',
                  transition: 'all 100ms', '&:hover': { bgcolor: 'action.hover' } }}>
                <Avatar sx={{ width: 36, height: 36, mx: 'auto', mb: 0.75,
                  bgcolor: config.channel === ch.key ? 'primary.main' : (t) => t.palette.mode === 'light' ? '#F1F5F9' : 'rgba(255,255,255,0.06)',
                  color: config.channel === ch.key ? '#fff' : 'text.secondary' }}>
                  <ch.Icon sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography sx={{ fontSize: 13, fontWeight: config.channel === ch.key ? 700 : 500 }}>{ch.label}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Recipients */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Recipients <Typography component="span" sx={{ color: 'error.main' }}>*</Typography></Typography>
          <Select value={config.notify_id ?? ''} onChange={(e) => { const u = users.find((u: any) => u.id === e.target.value) as any; onChange({ ...config, notify_id: e.target.value, notify_name: u?.name }) }} displayEmpty size="medium" fullWidth>
            <MenuItem value="">Add recipient...</MenuItem>
            <ListSubheader sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em' }}>DYNAMIC — RESOLVED AT RUN TIME</ListSubheader>
            <MenuItem value="__requester">
              <Tooltip title="The person who submitted the request or work order. Resolves from context.reporter_id (or user_id fallback). Requires: a request.submitted / work_order.* trigger.">
                <Chip label="Requester" size="small" sx={{ height: 24, fontWeight: 600 }} />
              </Tooltip>
            </MenuItem>
            <MenuItem value="__approver">
              <Tooltip title="The user assigned to the currently-pending approval step on this work order. Requires: an Approval step ran earlier in this workflow, or a manual approval exists on the WO.">
                <Chip label="Approver" size="small" sx={{ height: 24, fontWeight: 600 }} />
              </Tooltip>
            </MenuItem>
            <MenuItem value="__assignee">
              <Tooltip title="The current assignee of the work order in context. Resolves to null if the WO is unassigned; the step silently no-ops. Requires: a WO trigger, or an Assign step earlier in this workflow.">
                <Chip label="Assignee" size="small" sx={{ height: 24, fontWeight: 600 }} />
              </Tooltip>
            </MenuItem>
            <MenuItem value="__location_assignee">
              <Tooltip title="The default assignee configured on the location (Location settings → Default assignee). Resolves to null if unset or if the trigger doesn't carry a location. Great for routing requests by location owner without spelling out per-location branching.">
                <Chip label="Location owner" size="small" sx={{ height: 24, fontWeight: 600, bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              </Tooltip>
            </MenuItem>
            <MenuItem value="__site_manager">
              <Tooltip title="The manager configured on the site (sites.manager_id — legacy field). Requires: site_id in context and a manager set on that site. Falls back to null silently if the site doesn't have one.">
                <Chip label="Site manager" size="small" sx={{ height: 24, fontWeight: 600, bgcolor: '#DBEAFE', color: '#1E40AF' }} />
              </Tooltip>
            </MenuItem>
            <Divider />
            <ListSubheader sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em' }}>SPECIFIC USER</ListSubheader>
            {users.map((u: any) => <MenuItem key={u.id} value={u.id}><Stack direction="row" alignItems="center" spacing={1}><UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} /><Box><Typography sx={{ fontSize: 13 }}>{u.name}</Typography><Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{u.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography></Box></Stack></MenuItem>)}
          </Select>
          <Box sx={{ mt: 1 }}>
            <HintBox tone="info">
              <strong>Dynamic recipients</strong> resolve when the workflow runs — the actual person can change per event (e.g. "Location owner" picks whoever is set on each request's location). <strong>Specific users</strong> pin one person for every run. Hover a dynamic option above to see when it resolves.
            </HintBox>
          </Box>
        </Box>

        {/* Template */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Template</Typography>
          <Select value={config.template ?? ''} onChange={(e) => {
            const tpl = e.target.value
            set('template', tpl)
            if (tpl === 'wo_completed')   set('message', 'Work order "{{title}}" has been closed. The workflow completed successfully.')
            if (tpl === 'wo_assigned')    set('message', 'You have been assigned to work order "{{title}}" (priority: {{priority}}). Open it: {{work_order_url}}')
            if (tpl === 'approval_needed') set('message', 'Work order "{{title}}" needs your approval. Please review and respond: {{work_order_url}}')
            if (tpl === 'request_received')set('message', 'A new request came in from {{site_name}} — {{location_name}}: {{title}}. Open it: {{request_url}}')
          }} displayEmpty size="medium" fullWidth>
            <MenuItem value="">Custom message</MenuItem>
            <MenuItem value="wo_completed">Work order completed</MenuItem>
            <MenuItem value="wo_assigned">Work order assigned</MenuItem>
            <MenuItem value="approval_needed">Approval needed</MenuItem>
            <MenuItem value="request_received">Request received</MenuItem>
          </Select>
        </Box>

        {/* Subject */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Subject (optional)</Typography>
          <TextField value={config.subject ?? ''} onChange={(e) => set('subject', e.target.value)}
            size="medium" fullWidth
            placeholder="e.g. [{{priority}}] {{title}}"
            helperText="Shown as the notification title / email subject. Defaults to the workflow's title if blank." />
        </Box>

        <FormatToggle value={config.format} onChange={(v) => set('format', v)} />

        {/* Message */}
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Message</Typography>
          <TextField value={config.message ?? ''} onChange={(e) => set('message', e.target.value)} size="medium" fullWidth multiline minRows={4}
            placeholder={config.format === 'markdown'
              ? 'e.g. **New {{priority}} request** from {{site_name}}\\n\\n- Location: {{location_name}}\\n- [Open it]({{request_url}})'
              : 'e.g. New {{priority}} request from {{site_name}}: {{title}}. Open it: {{request_url}}'}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5, lineHeight: 1.6 } }} />
        </Box>

        <MessagePreview body={config.message ?? ''} subject={config.subject} format={config.format} tone="notification" />

        <HintBox tone="info">
          <strong>Available tokens</strong> (subject + message):{' '}
          <code style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 10.5 }}>
            {`{{title}} {{description}} {{priority}} {{status}} {{source}} {{site_name}} {{location_name}} {{asset_name}} {{submitted_by_name}} {{submitted_by_email}} {{wo_code}} {{work_order_id}} {{request_url}} {{work_order_url}}`}
          </code>
          . Unknown tokens render as literal <code>{'{{typo}}'}</code> in the preview above so misspellings are caught early. Dotted paths like <code>{'{{wo.title}}'}</code> are NOT supported — use flat names.
        </HintBox>
      </Stack>)
    case 'email':
      return (<Stack spacing={2}>
        <TextField label="To" required value={config.to ?? ''} onChange={(e) => set('to', e.target.value)}
          size="small" fullWidth
          placeholder="oncall@example.com or {{submitted_by_email}}"
          helperText="Any address. Supports {{tokens}} — great for routing back to the requester." />
        <TextField label="Subject" required value={config.subject ?? ''} onChange={(e) => set('subject', e.target.value)}
          size="small" fullWidth
          placeholder="e.g. [{{priority}}] New request: {{title}}" />

        <FormatToggle value={config.format} onChange={(v) => set('format', v)} />

        <TextField label="Body" value={config.body ?? ''} onChange={(e) => set('body', e.target.value)}
          size="small" fullWidth multiline minRows={5}
          placeholder={config.format === 'markdown'
            ? 'Hi,\\n\\nA new **{{priority}}** request came in from *{{site_name}}*:\\n\\n- Location: {{location_name}}\\n- Reported by: {{submitted_by_name}}\\n\\n[Open the request]({{request_url}})'
            : 'Hi,\\n\\nA new request just came in from {{site_name}} - {{location_name}}:\\n\\n{{description}}\\n\\nOpen: {{request_url}}'} />

        <MessagePreview body={config.body ?? ''} subject={config.subject} format={config.format} tone="email" />

        <HintBox tone="info">
          <strong>Available tokens</strong> (usable in To / Subject / Body):{' '}
          <code style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 10.5 }}>
            {`{{title}} {{description}} {{priority}} {{status}} {{source}} {{site_name}} {{location_name}} {{asset_name}} {{submitted_by_name}} {{submitted_by_email}} {{reporter_id}} {{wo_code}} {{work_order_id}} {{request_url}} {{work_order_url}}`}
          </code>
          . Unknown tokens render as literal <code>{'{{typo}}'}</code> in the preview above so misspellings are caught early.
        </HintBox>
      </Stack>)
    case 'assign':
      return (<Stack spacing={2.5}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Assign to user</Typography>
          <SearchableSelect
            value={config.assignee_id ?? ''}
            onChange={(uid) => { const u = users.find((u: any) => u.id === uid) as any; onChange({ ...config, assignee_id: uid, assignee_name: u?.name }) }}
            options={users}
            getValue={(u: any) => u.id}
            getLabel={(u: any) => u.name}
            placeholder="No user"
            renderOption={(u: any) => (
              <Stack direction="row" alignItems="center" spacing={1}>
                <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} />
                <Box>
                  <Typography sx={{ fontSize: 13 }}>{u.name}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{u.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                </Box>
              </Stack>
            )}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Or assign to team</Typography>
          <SearchableSelect
            value={config.team_id ?? ''}
            onChange={(tid) => { const t = teams.find((t: any) => t.id === tid) as any; onChange({ ...config, team_id: tid, team_name: t?.name }) }}
            options={teams}
            getValue={(t: any) => t.id}
            getLabel={(t: any) => t.name}
            placeholder="No team"
          />
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>
            Pick a user, a team, or both — at least one is required.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Switch size="small" checked={config.only_if_unassigned !== false} onChange={(e) => set('only_if_unassigned', e.target.checked)} />
          <Box>
            <Typography sx={{ fontSize: 13 }}>Only if unassigned</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>Skip this step if someone is already assigned to the work order</Typography>
          </Box>
        </Stack>
      </Stack>)
    case 'update_field':
      return (<Stack spacing={2}>
        <Select value={config.field ?? ''} onChange={(e) => onChange({ ...config, field: e.target.value, value: '' })} displayEmpty size="small" fullWidth>
          <MenuItem value="">Select field <span style={{ color: '#d32f2f', marginLeft: 4 }}>*</span></MenuItem>
          <MenuItem value="priority">Priority</MenuItem>
          <MenuItem value="status">Status</MenuItem>
          <MenuItem value="work_type">Work type</MenuItem>
          <MenuItem value="category">Category</MenuItem>
        </Select>
        <DynamicValuePicker field={config.field ?? ''} value={config.value ?? ''} onChange={(v) => set('value', v)} categories={categories} />
        <HintBox tone="info">
          Only these four fields are mutable via workflows for safety — audit trail, notifications, and downstream automations rely on other columns being immutable at run time. To change site / location / assignee, use dedicated steps: <em>Create work order</em> (with a picker), <em>Assign</em>, or a manual update.
        </HintBox>
        {config.field === 'status' && (config.value === 'closed' || config.value === 'resolved') && (
          <HintBox tone="warn">
            <strong>⚠️ Setting status to {config.value} bypasses the normal close flow.</strong> Signature-required WOs, resolution notes, spawn-next-occurrence, and close notifications will NOT fire when done through this step. Only use for admin-driven cleanup — for normal closes, drive it from the WO detail page.
          </HintBox>
        )}
      </Stack>)
    case 'attach_checklist':
      return (<Stack spacing={2.5}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Checklist template <Typography component="span" sx={{ color: 'error.main' }}>*</Typography></Typography>
          <SearchableSelect
            value={config.template_id ?? ''}
            onChange={(tid) => { const c = checklists.find((c: any) => c.id === tid); onChange({ ...config, template_id: tid, template_name: c?.name }) }}
            options={checklists}
            getValue={(c: any) => c.id}
            getLabel={(c: any) => c.name}
            placeholder="Select a checklist"
            renderOption={(c: any) => (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <ChecklistOutlinedIcon sx={{ fontSize: 16, color: '#059669' }} />
                <Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
              </Stack>
            )}
          />
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>This checklist will be attached to the work order so the technician must complete it.</Typography>
        </Box>
        <HintBox tone="info">
          If the WO already has a primary checklist, this template is added to the multi-link table so both surface in the mobile executor. Otherwise it becomes the primary. Attaching the same template twice is a no-op.
        </HintBox>
      </Stack>)
    case 'condition':
      return (
        <ConditionEditor
          config={config} onChange={onChange}
          users={users} categories={categories}
          sites={sites} locations={conditionLocations}
          triggerType={triggerType}
        />
      )
    case 'delay': {
      const ms = durationMs(config.duration, config.unit ?? 'hours')
      const overCap = ms > INLINE_WAIT_CAP_MS
      return (<Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5}>
          <TextField label="Duration" required type="number" value={config.duration ?? ''} onChange={(e) => set('duration', e.target.value)} size="small" sx={{ width: 120 }} />
          <Select value={config.unit ?? 'hours'} onChange={(e) => set('unit', e.target.value)} size="small" sx={{ flex: 1 }}>
            <MenuItem value="minutes">Minutes</MenuItem>
            <MenuItem value="hours">Hours</MenuItem>
            <MenuItem value="days">Days</MenuItem>
          </Select>
        </Stack>
        {overCap ? (
          <HintBox tone="danger">
            <strong>⚠️ Delays longer than 5 minutes are not supported yet.</strong> This step will fall through immediately without waiting — long waits need a background job queue that isn't wired up in this build. Use an <em>Escalation timer</em> for "wait X, then check something" flows, or split the workflow across a <em>Scheduled</em> trigger.
          </HintBox>
        ) : (
          <HintBox tone="info">
            Waits inline before the next step runs. Kept under 5 minutes because the engine blocks the request thread during the wait.
          </HintBox>
        )}
      </Stack>)
    }

    case 'webhook':
      return (<Stack spacing={2.5}>
        <TextField label="URL" required value={config.url ?? ''} onChange={(e) => set('url', e.target.value)} size="small" fullWidth
          placeholder="https://hooks.slack.com/services/..." helperText="The endpoint to call when this step runs" />
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Method</Typography>
            <Select value={config.method ?? 'POST'} onChange={(e) => set('method', e.target.value)} size="small" fullWidth>
              <MenuItem value="POST">POST</MenuItem>
              <MenuItem value="GET">GET</MenuItem>
              <MenuItem value="PUT">PUT</MenuItem>
              <MenuItem value="PATCH">PATCH</MenuItem>
            </Select>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Authentication</Typography>
            <Select value={config.auth_type ?? 'none'} onChange={(e) => set('auth_type', e.target.value)} size="small" fullWidth>
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="bearer">Bearer token</MenuItem>
              <MenuItem value="basic">Basic auth</MenuItem>
            </Select>
          </Box>
        </Stack>
        {config.auth_type === 'bearer' && (
          <TextField label="Bearer token" value={config.auth_token ?? ''} onChange={(e) => set('auth_token', e.target.value)} size="small" fullWidth type="password" />
        )}
        {config.auth_type === 'basic' && (
          <Stack direction="row" spacing={2}>
            <TextField label="Username" value={config.auth_user ?? ''} onChange={(e) => set('auth_user', e.target.value)} size="small" fullWidth />
            <TextField label="Password" value={config.auth_pass ?? ''} onChange={(e) => set('auth_pass', e.target.value)} size="small" fullWidth type="password" />
          </Stack>
        )}
        <TextField label="Payload (JSON)" value={config.payload_template ?? ''} onChange={(e) => set('payload_template', e.target.value)}
          size="small" fullWidth multiline minRows={3} placeholder='{"text": "WO {{title}} was created with priority {{priority}}"}'
          helperText="Leave empty for auto-generated payload. Supports {{variables}}." />
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Switch size="small" checked={config.fail_on_error !== false} onChange={(e) => set('fail_on_error', e.target.checked)} />
          <Box>
            <Typography sx={{ fontSize: 13 }}>Fail workflow on error</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>Stop the workflow if the webhook returns an error</Typography>
          </Box>
        </Stack>
      </Stack>)

    case 'if_else': {
      const thenSteps: any[] = Array.isArray(config.then_steps) ? config.then_steps : []
      const elseSteps: any[] = Array.isArray(config.else_steps) ? config.else_steps : []

      function addSubStep(branch: 'then_steps' | 'else_steps', stepType: string) {
        const existing = Array.isArray(config[branch]) ? config[branch] : []
        const sm = STEP_MAP[stepType]
        onChange({ ...config, [branch]: [...existing, { step_type: stepType, label: sm?.label ?? stepType, config: {} }] })
      }
      function removeSubStep(branch: 'then_steps' | 'else_steps', idx: number) {
        const arr = [...(Array.isArray(config[branch]) ? config[branch] : [])]
        arr.splice(idx, 1)
        onChange({ ...config, [branch]: arr })
      }
      function updateSubStep(branch: 'then_steps' | 'else_steps', idx: number, updates: any) {
        const arr = [...(Array.isArray(config[branch]) ? config[branch] : [])]
        arr[idx] = { ...arr[idx], ...updates }
        onChange({ ...config, [branch]: arr })
      }

      const SUB_STEP_OPTIONS = STEP_TYPES.filter(s => !['if_else', 'escalation_timer', 'repeat_until', 'condition'].includes(s.key))

      return (<Stack spacing={2.5}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Condition</Typography>
        <ConditionEditor
          config={config} onChange={onChange}
          users={users} categories={categories}
          sites={sites} locations={conditionLocations}
          triggerType={triggerType}
        />

        <Divider />

        {/* THEN branch */}
        <BranchStepList
          label="THEN — if condition is true"
          color="#059669"
          steps={thenSteps}
          branchKey="then_steps"
          options={SUB_STEP_OPTIONS}
          onAdd={addSubStep}
          onRemove={removeSubStep}
          onUpdate={updateSubStep}
          triggerType={triggerType}
          customerId={customerId}
        />

        {/* ELSE branch */}
        <BranchStepList
          label="ELSE — if condition is false"
          color="#DC2626"
          steps={elseSteps}
          branchKey="else_steps"
          options={SUB_STEP_OPTIONS}
          onAdd={addSubStep}
          onRemove={removeSubStep}
          onUpdate={updateSubStep}
          triggerType={triggerType}
          customerId={customerId}
        />
      </Stack>)
    }

    case 'escalation_timer': {
      const escSteps: any[] = Array.isArray(config.escalation_steps) ? config.escalation_steps : []
      function addEsc(_branch: 'then_steps' | 'else_steps', stepType: string) {
        const sm = STEP_MAP[stepType]
        onChange({ ...config, escalation_steps: [...escSteps, { step_type: stepType, label: sm?.label ?? stepType, config: {} }] })
      }
      function removeEsc(_branch: 'then_steps' | 'else_steps', idx: number) {
        const arr = [...escSteps]; arr.splice(idx, 1)
        onChange({ ...config, escalation_steps: arr })
      }
      function updateEsc(_branch: 'then_steps' | 'else_steps', idx: number, updates: any) {
        const arr = [...escSteps]; arr[idx] = { ...arr[idx], ...updates }
        onChange({ ...config, escalation_steps: arr })
      }
      const SUB_STEP_OPTIONS = STEP_TYPES.filter(s => !['if_else', 'escalation_timer', 'repeat_until', 'condition'].includes(s.key))
      const waitMs = durationMs(config.wait_duration, config.wait_unit ?? 'hours')
      const waitOverCap = waitMs > INLINE_WAIT_CAP_MS
      return (<Stack spacing={2.5}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Wait period</Typography>
        <Stack direction="row" spacing={1.5}>
          <TextField label="Wait" required type="number" value={config.wait_duration ?? ''} onChange={(e) => set('wait_duration', e.target.value)} size="small" sx={{ width: 120 }} />
          <Select value={config.wait_unit ?? 'hours'} onChange={(e) => set('wait_unit', e.target.value)} size="small" sx={{ flex: 1 }}>
            <MenuItem value="minutes">Minutes</MenuItem>
            <MenuItem value="hours">Hours</MenuItem>
            <MenuItem value="days">Days</MenuItem>
          </Select>
        </Stack>
        {waitOverCap ? (
          <HintBox tone="warn">
            <strong>⚠️ Wait longer than 5 minutes will be capped.</strong> The engine currently caps escalation waits at 5 minutes because there's no background job queue. Your escalation will fire 5 minutes after the workflow starts, not after {config.wait_duration} {config.wait_unit ?? 'hours'}. Everything else works — the condition check + escalation actions still run as configured.
          </HintBox>
        ) : (
          <HintBox tone="info">
            After the wait, the engine re-reads the work order and only runs the escalation actions if the check condition below is still true. If the WO was already resolved, this step is skipped.
          </HintBox>
        )}
        <Divider />
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Check condition after wait</Typography>
        <Stack direction="row" spacing={1.5}>
          <Select value={config.check_field ?? 'status'}
            onChange={(e) => onChange({ ...config, check_field: e.target.value, check_value: '' })}
            size="small" sx={{ flex: 1 }}>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="assigned_to_user_id">Assignee</MenuItem>
            <MenuItem value="priority">Priority</MenuItem>
          </Select>
          <Typography sx={{ fontSize: 13, alignSelf: 'center', color: 'text.secondary' }}>still equals</Typography>
          <Box sx={{ flex: 1 }}>
            <CheckValuePicker
              field={config.check_field ?? 'status'}
              value={config.check_value ?? ''}
              onChange={(v) => set('check_value', v)}
              users={users} />
          </Box>
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
          If the condition is still true after the wait, these escalation actions run. If already resolved, this step is skipped.
        </Typography>
        <Divider />
        <BranchStepList
          label="ESCALATION ACTIONS — run when condition still holds"
          color="#DC2626"
          steps={escSteps}
          branchKey="then_steps"
          options={SUB_STEP_OPTIONS}
          onAdd={addEsc}
          onRemove={removeEsc}
          onUpdate={updateEsc}
          customerId={customerId}
        />
      </Stack>)
    }

    case 'repeat_until': {
      const repSteps: any[] = Array.isArray(config.repeat_steps) ? config.repeat_steps : []
      function addRep(_branch: 'then_steps' | 'else_steps', stepType: string) {
        const sm = STEP_MAP[stepType]
        onChange({ ...config, repeat_steps: [...repSteps, { step_type: stepType, label: sm?.label ?? stepType, config: {} }] })
      }
      function removeRep(_branch: 'then_steps' | 'else_steps', idx: number) {
        const arr = [...repSteps]; arr.splice(idx, 1)
        onChange({ ...config, repeat_steps: arr })
      }
      function updateRep(_branch: 'then_steps' | 'else_steps', idx: number, updates: any) {
        const arr = [...repSteps]; arr[idx] = { ...arr[idx], ...updates }
        onChange({ ...config, repeat_steps: arr })
      }
      const SUB_STEP_OPTIONS = STEP_TYPES.filter(s => !['if_else', 'escalation_timer', 'repeat_until', 'condition'].includes(s.key))
      const intervalMs = durationMs(config.interval_duration, config.interval_unit ?? 'hours')
      const intervalOverCap = intervalMs > INLINE_WAIT_CAP_MS
      return (<Stack spacing={2.5}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Repeat interval</Typography>
        <Stack direction="row" spacing={1.5}>
          <TextField label="Every" required type="number" value={config.interval_duration ?? ''} onChange={(e) => set('interval_duration', e.target.value)} size="small" sx={{ width: 120 }} />
          <Select value={config.interval_unit ?? 'hours'} onChange={(e) => set('interval_unit', e.target.value)} size="small" sx={{ flex: 1 }}>
            <MenuItem value="minutes">Minutes</MenuItem>
            <MenuItem value="hours">Hours</MenuItem>
            <MenuItem value="days">Days</MenuItem>
          </Select>
        </Stack>
        <TextField label="Max iterations" type="number" value={config.max_iterations ?? '3'} onChange={(e) => set('max_iterations', e.target.value)}
          size="small" fullWidth helperText="Hard limit to prevent infinite loops. Values above 10 are silently clamped to 10." />
        {intervalOverCap ? (
          <HintBox tone="warn">
            <strong>⚠️ Intervals longer than 5 minutes will be capped.</strong> The engine caps the wait between iterations at 5 minutes (no background job queue yet). If you set "Every 1 hour", iterations will actually run 5 minutes apart. Reduce max iterations accordingly, or use a Scheduled workflow instead.
          </HintBox>
        ) : (
          <HintBox tone="info">
            Repeats the actions below on the interval until the stop condition is met OR max iterations is reached — whichever comes first. Useful for reminder loops like "notify tech every 30 min until they accept."
          </HintBox>
        )}
        <Divider />
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Stop when</Typography>
        <Stack direction="row" spacing={1.5}>
          <Select value={config.stop_field ?? 'status'}
            onChange={(e) => onChange({ ...config, stop_field: e.target.value, stop_values: '' })}
            size="small" sx={{ flex: 1 }}>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="assigned_to_user_id">Assignee</MenuItem>
            <MenuItem value="priority">Priority</MenuItem>
          </Select>
          <Typography sx={{ fontSize: 13, alignSelf: 'center', color: 'text.secondary' }}>is one of</Typography>
          <Box sx={{ flex: 1.5 }}>
            <StopValuesPicker
              field={config.stop_field ?? 'status'}
              value={config.stop_values ?? ''}
              onChange={(v) => set('stop_values', v)}
              users={users} />
          </Box>
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
          Each iteration re-checks this condition. When met, the loop stops. Example: repeat reminders until status is "closed" or "resolved".
        </Typography>
        <Divider />
        <BranchStepList
          label="REPEATING ACTIONS — run on every iteration"
          color="#7C3AED"
          steps={repSteps}
          branchKey="then_steps"
          options={SUB_STEP_OPTIONS}
          onAdd={addRep}
          onRemove={removeRep}
          onUpdate={updateRep}
          customerId={customerId}
        />
      </Stack>)
    }

    case 'create_work_order':
    case 'create_task': {
      // Same UI for both — one variable gates the asset / vendor
      // rows (tasks reject those at the service layer) and drives every
      // human-facing string. Two labels (lowercase for mid-sentence,
      // capitalised for headings / list-item titles) let the copy read
      // naturally without ternaries scattered through every line.
      const isTaskStep = stepType === 'create_task'
      const kindLabel = isTaskStep ? 'task' : 'work order'
      const KindLabel = isTaskStep ? 'Task' : 'Work order'
      const codeLabel = isTaskStep ? 'TSK-00042' : 'WO-00123'
      return (<Stack spacing={2.5}>
        {/* General */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>General</Typography>
        <TextField label="Title" required value={config.title ?? ''} onChange={(e) => set('title', e.target.value)} size="small" fullWidth
          placeholder="e.g. Replace HVAC filter on Unit A-101 or Auto: {{title}}" />
        <TextField label="Description" value={config.description ?? ''} onChange={(e) => set('description', e.target.value)} size="small" fullWidth multiline minRows={2}
          placeholder="Describe what needs to be done, or reference request fields with {{description}}, {{site_name}}, {{location_name}}..."
          disabled={config.inherit_description === true}
          helperText={config.inherit_description === true ? 'Description will be copied verbatim from the source request.' : undefined} />
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Switch size="small"
            checked={config.inherit_description === true}
            onChange={(e) => set('inherit_description', e.target.checked)} />
          <Box>
            <Typography sx={{ fontSize: 13 }}>Copy description from source request</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>On = the request's raw description becomes the {kindLabel} description, overriding the field above. Off = use the field above (with tokens). Only applies when the trigger is Request submitted; on other triggers it's a no-op.</Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Switch size="small"
            checked={config.link_to_request === true}
            onChange={(e) => set('link_to_request', e.target.checked)} />
          <Box>
            <Typography sx={{ fontSize: 13 }}>Link the new {isTaskStep ? 'task' : 'WO'} back to the source request</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>On = the request page shows "Linked {isTaskStep ? 'task' : 'WO'}: {codeLabel}" and can jump to it. Off = the {kindLabel} exists standalone, no back-pointer. Only fires when the trigger is Request submitted.</Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Switch size="small"
            checked={config.copy_request_attachments === true}
            onChange={(e) => set('copy_request_attachments', e.target.checked)} />
          <Box>
            <Typography sx={{ fontSize: 13 }}>Copy photos from the request onto the new {kindLabel}</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>On = every photo the submitter attached lands on the new {kindLabel}. Off = only pre-configured attachments below get copied. Only fires when the trigger is Request submitted.</Typography>
          </Box>
        </Stack>
        <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: (t) => t.palette.mode === 'light' ? '#F0FDF4' : 'rgba(16,185,129,0.06)', border: '1px solid', borderColor: (t) => t.palette.mode === 'light' ? '#BBF7D0' : 'rgba(16,185,129,0.2)' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: (t) => t.palette.mode === 'light' ? '#166534' : '#86EFAC', mb: 0.5 }}>Available tokens (Title & Description fields only)</Typography>
          <Typography sx={{ fontSize: 11, color: (t) => t.palette.mode === 'light' ? '#166534' : '#86EFAC', lineHeight: 1.65, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
            {isTaskStep
              ? `{{title}} {{description}} {{priority}} {{source}} {{site_name}} {{location_name}} {{submitted_by_name}} {{submitted_by_email}} {{task_code}} {{task_id}} {{task_url}} {{request_url}}`
              : `{{title}} {{description}} {{priority}} {{source}} {{site_name}} {{location_name}} {{asset_name}} {{submitted_by_name}} {{submitted_by_email}} {{wo_code}} {{work_order_id}} {{request_url}}`}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: (t) => t.palette.mode === 'light' ? '#166534' : '#86EFAC', mt: 0.5, fontStyle: 'italic' }}>
            These interpolate string fields only — Title, Description. To pick a specific site / location / asset / assignee, use the dropdowns below (they take actual entities, not tokens).
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Switch size="small"
            checked={config.inherit_priority === true}
            onChange={(e) => set('inherit_priority', e.target.checked)} />
          <Box>
            <Typography sx={{ fontSize: 13 }}>Inherit priority from the source request</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>On = the {kindLabel} gets the request's priority verbatim (a critical request becomes a critical {kindLabel}). Off = use the picker below. Only meaningful for request.submitted / work_order.* triggers that carry a priority.</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Select
              value={config.priority ?? 'medium'}
              onChange={(e) => set('priority', e.target.value)}
              size="small" fullWidth
              disabled={config.inherit_priority === true}
              renderValue={(v: string) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: PRIORITY_COLORS[v] ?? '#94A3B8' }} />
                  <span style={{ textTransform: 'capitalize' }}>{v} priority</span>
                </Stack>
              )}>
              {['low', 'medium', 'high', 'critical'].map((p) => (
                <MenuItem key={p} value={p}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: PRIORITY_COLORS[p] }} />
                    <Typography sx={{ fontSize: 13, textTransform: 'capitalize' }}>{p} priority</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Select value={config.work_type ?? 'reactive'} onChange={(e) => set('work_type', e.target.value)} size="small" fullWidth>
              <MenuItem value="reactive">Reactive</MenuItem>
              <MenuItem value="preventive">Preventive</MenuItem>
              <MenuItem value="inspection">Inspection</MenuItem>
              <MenuItem value="safety">Safety</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </Box>
        </Stack>

        <Divider />
        {/* Assignment */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Assignment</Typography>
        <SearchableSelect
          value={config.assignee_id ?? ''}
          onChange={(uid) => { const u = users.find((u: any) => u.id === uid) as any; onChange({ ...config, assignee_id: uid, assignee_name: u?.name }) }}
          options={users}
          getValue={(u: any) => u.id}
          getLabel={(u: any) => u.name}
          placeholder="Assign to person"
          renderOption={(u: any) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} />
              <Box><Typography sx={{ fontSize: 13 }}>{u.name}</Typography><Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{u.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography></Box>
            </Stack>
          )}
        />

        <SearchableSelect
          value={config.team_id ?? ''}
          onChange={(tid) => { const t = teams.find((t: any) => t.id === tid) as any; onChange({ ...config, team_id: tid, team_name: t?.name }) }}
          options={teams}
          getValue={(t: any) => t.id}
          getLabel={(t: any) => t.name}
          placeholder="Assign to team"
          renderOption={(t: any) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar variant="rounded" sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 700, bgcolor: '#7C3AED' }}><GroupsOutlinedIcon sx={{ fontSize: 14 }} /></Avatar>
              <Box><Typography sx={{ fontSize: 13 }}>{t.name}</Typography><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{t.member_count} members</Typography></Box>
            </Stack>
          )}
        />

        {/* Reporter — who "owns" the WO / task, gets close-notifications, etc.
            Supports the same dynamic tokens as the notification recipient
            picker so admins can say "reporter = whoever submitted the
            request" without hardcoding a user. Falls back to the
            triggering user if left blank. */}
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Reporter</Typography>
          <Select value={config.reporter_id ?? ''}
            onChange={(e) => {
              const v = e.target.value
              const u = users.find((u: any) => u.id === v) as any
              onChange({ ...config, reporter_id: v, reporter_name: u?.name })
            }}
            displayEmpty size="small" fullWidth>
            <MenuItem value="">Default — the person who triggered the workflow</MenuItem>
            <ListSubheader sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em' }}>DYNAMIC — RESOLVED AT RUN TIME</ListSubheader>
            <MenuItem value="__requester">
              <Tooltip title={`Whoever submitted the source request. Best pick when the trigger is Request submitted — the new ${kindLabel}'s reporter matches the request's reporter, so close-emails go to the right person.`}>
                <Chip label="Requester (from source request)" size="small" sx={{ height: 22, bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 600 }} />
              </Tooltip>
            </MenuItem>
            <Divider />
            <ListSubheader sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em' }}>SPECIFIC USER</ListSubheader>
            {users.map((u: any) => (
              <MenuItem key={u.id} value={u.id}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} />
                  <Box>
                    <Typography sx={{ fontSize: 13 }}>{u.name}</Typography>
                    <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{u.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                  </Box>
                </Stack>
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5 }}>
            The {kindLabel}'s reporter drives who receives close / acknowledge emails and who shows up as "Reported by" on the detail page. Leave blank to auto-fill with the workflow's triggering user.
          </Typography>
        </Box>

        <Divider />
        {/* Location & Asset */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Location & Asset</Typography>
        <HintBox tone="info">
          <strong>How the pickers work:</strong> pick a specific site / location to <em>always</em> use that one, regardless of the trigger. Leave them <strong>empty</strong> to inherit from the source request instead (see the switch below).{' '}
          <strong>Do not type tokens like <code>{'{{site_name}}'}</code> in the pickers</strong> — those only work in text fields (Title, Description, Email subject/body). The pickers expect an actual site/location.
        </HintBox>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Switch size="small"
            checked={config.inherit_location !== false}
            onChange={(e) => set('inherit_location', e.target.checked)} />
          <Box>
            <Typography sx={{ fontSize: 13 }}>Inherit the trigger's location when the picker below is empty</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>On (default) = use whatever location the triggering event carries as a fallback — a request, work order, or asset (e.g. a soft point reading's asset), if that trigger provides one. Off = leave the {kindLabel}'s location empty. <strong>The site always falls back to the trigger's site regardless of this switch</strong> — {kindLabel}s need a site to be valid, so we never leave it blank.</Typography>
          </Box>
        </Stack>
        <SearchableSelect
          value={config.site_id ?? ''}
          onChange={(sid) => { onChange({ ...config, site_id: sid, location_id: '', asset_id: '' }) }}
          options={sites}
          getValue={(s: any) => s.id}
          getLabel={formatSiteName}
          placeholder="Site — leave empty to inherit from the trigger"
          renderOption={(s: any) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
              <Typography sx={{ fontSize: 13 }}>{s.name}</Typography>
            </Stack>
          )}
        />

        <SearchableSelect
          value={config.location_id ?? ''}
          onChange={(lid) => set('location_id', lid)}
          options={createWoLocations}
          getValue={(l: any) => l.id}
          getLabel={(l: any) => l.name}
          placeholder={config.site_id ? 'Location — leave empty to inherit from the trigger' : 'Pick a site to load locations (or leave empty to inherit)'}
          renderOption={(l: any) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#8B5CF6' }} />
              <Box>
                <Typography sx={{ fontSize: 13 }}>{l.name}</Typography>
                {l.type && <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'capitalize' }}>{l.type}</Typography>}
              </Box>
            </Stack>
          )}
        />

        {!isTaskStep && <SearchableSelect
          value={config.asset_id ?? ''}
          onChange={(assetId) => {
            const asset = assets.find((a: any) => a.id === assetId) as any
            const updates: Record<string, any> = { ...config, asset_id: assetId }
            if (asset) {
              if (asset.site_id && !config.site_id) updates.site_id = asset.site_id
              if (asset.category_name && !config.category) updates.category = asset.category_name
              const meta = (asset.metadata ?? {}) as Record<string, any>
              if (meta.vendor_id && !config.vendor_id) updates.vendor_id = meta.vendor_id
            }
            onChange(updates)
            // Auto-fetch linked checklists from the asset
            if (assetId) {
              api.assets.listChecklists(assetId).then((cls: any[]) => {
                const ids = (cls ?? []).map((c: any) => c.template_id)
                if (ids.length > 0) {
                  const existing = Array.isArray(config.checklist_ids) ? config.checklist_ids : []
                  onChange({ ...updates, checklist_ids: [...new Set([...existing, ...ids])] })
                }
              }).catch(() => {})
            }
          }}
          options={filteredAssets}
          getValue={(a: any) => a.id}
          getLabel={(a: any) => `${a.name} (${a.asset_code})`}
          placeholder="Select asset — leave empty to inherit from the trigger (e.g. the asset a soft point reading came from)"
          renderOption={(a: any) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar variant="rounded" sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 700, bgcolor: '#EEF2FF', color: '#6366F1' }}>{a.asset_code?.slice(-2)}</Avatar>
              <Box><Typography sx={{ fontSize: 13 }}>{a.name}</Typography><Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{a.asset_code}</Typography></Box>
            </Stack>
          )}
        />}
        {isTaskStep && (
          <HintBox tone="info">
            <strong>Tasks don't take an asset or vendor</strong> — they're soft-services / internal work. If you need to link a piece of equipment or an external contractor, use <em>Create work order</em> instead.
          </HintBox>
        )}

        <Divider />
        {/* Schedule */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Schedule</Typography>
        <Stack direction="row" spacing={2}>
          <TextField label="Start date" type="date" value={config.start_date ?? ''} onChange={(e) => set('start_date', e.target.value)}
            size="small" fullWidth InputLabelProps={{ shrink: true }} />
          <TextField label="Due date" type="date" value={config.due_date ?? ''} onChange={(e) => set('due_date', e.target.value)}
            size="small" fullWidth InputLabelProps={{ shrink: true }} />
        </Stack>
        <Stack direction="row" spacing={2}>
          <TextField label="Est. hours" type="number" value={config.est_hours ?? ''} onChange={(e) => set('est_hours', e.target.value)}
            size="small" fullWidth placeholder="0" />
          <TextField label="Est. minutes" type="number" value={config.est_mins ?? ''} onChange={(e) => set('est_mins', e.target.value)}
            size="small" fullWidth placeholder="0" />
        </Stack>

        <Divider />
        {/* Schedule — calendar-driven. Matches the main Create WO dialog:
            off = the row activates immediately at workflow-trigger time;
            one_shot = it sits in 'scheduled' until its activation time;
            recurring = it becomes a template that spawns child rows on
            a calendar. Applies identically to WOs and tasks. */}
        <WorkflowScheduleSection config={config} set={set} onChange={onChange} kindLabel={kindLabel} />

        <Divider />
        {/* Linked items */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Linked Items</Typography>
        <SearchableSelect
          multiple
          value={config.checklist_ids ?? []}
          onChange={(ids) => set('checklist_ids', ids)}
          options={checklists}
          getValue={(c: any) => c.id}
          getLabel={(c: any) => c.name}
          placeholder="Select checklists"
          renderOption={(c: any) => (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
              <ChecklistOutlinedIcon sx={{ fontSize: 16, color: (config.checklist_ids ?? []).includes(c.id) ? '#8B5CF6' : 'text.secondary' }} />
              <Typography sx={{ fontSize: 13, flex: 1 }}>{c.name}</Typography>
              {(config.checklist_ids ?? []).includes(c.id) && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#059669' }} />}
            </Stack>
          )}
        />

        {!isTaskStep && <SearchableSelect
          value={config.vendor_id ?? ''}
          onChange={(vid) => { const v = vendors.find((v: any) => v.id === vid) as any; onChange({ ...config, vendor_id: vid, vendor_name: v?.name }) }}
          options={vendors}
          getValue={(v: any) => v.id}
          getLabel={(v: any) => v.name}
          placeholder="No vendor"
          renderOption={(v: any) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar variant="rounded" sx={{ width: 22, height: 22, fontSize: 10, fontWeight: 700, bgcolor: '#FFF7ED', color: '#EA580C' }}>{v.name?.charAt(0).toUpperCase()}</Avatar>
              <Box><Typography sx={{ fontSize: 13 }}>{v.name}</Typography>{v.contact_name && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{v.contact_name}</Typography>}</Box>
            </Stack>
          )}
        />}

        <SearchableSelect
          value={config.category ?? ''}
          onChange={(c) => set('category', c)}
          options={categories}
          getValue={(c: any) => c.name}
          getLabel={(c: any) => c.name}
          placeholder="Select category"
          renderOption={(c: any) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: c.color ?? '#94A3B8' }} />
              <Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
            </Stack>
          )}
        />

        <SearchableSelect
          value={config.report_template_id ?? ''}
          onChange={(tid) => { const t = reportTemplates.find((t: any) => t.id === tid); onChange({ ...config, report_template_id: tid, report_template_name: t?.name }) }}
          options={reportTemplates}
          getValue={(t: any) => t.id}
          getLabel={(t: any) => t.name}
          placeholder="Report template — use org default"
          renderOption={(t: any) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: 13, flex: 1 }}>{t.name}</Typography>
              {t.is_default && <Chip label="Default" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />}
            </Stack>
          )}
        />

        <Divider />
        {/* Attachments */}
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Attachments</Typography>
        <StepAttachments attachments={config.attachments ?? []} onChange={(atts) => set('attachments', atts)} workflowId={workflowId} stepId={stepId} />
      </Stack>)
    }

    // Switch — a lightweight editor: pick the field, list value → label,
    // and let the branch's step list be composed inline via the same
    // WorkflowBuilder path (kept flat here for MVP; nested step edit is
    // a future improvement). Cases are stored as `{ value, label }` and
    // the engine matches by value at run time.
    case 'switch': {
      // The case value shape depends on the routing field: entity fields
      // get a picker, string enums get a select, everything else falls
      // back to a text input. Kept inline (not extracted) so per-field
      // logic reads top-to-bottom.
      const cases = Array.isArray(config.cases) ? config.cases : []
      const setCase = (i: number, patch: Record<string, any>) => {
        const next = [...cases]; next[i] = { ...next[i], ...patch }; set('cases', next)
      }
      const renderCaseValue = (cs: any, i: number) => {
        const onValue = (v: string, extra?: Record<string, any>) => setCase(i, { value: v, ...(extra ?? {}) })
        if (config.field === 'site_id') {
          return (
            <SearchableSelect
              value={cs.value ?? ''}
              onChange={(id) => { const s = sites.find((s: any) => s.id === id); onValue(id, { label: cs.label || (s ? formatSiteName(s) : '') }) }}
              options={sites}
              getValue={(s: any) => s.id}
              getLabel={formatSiteName}
              placeholder="Pick a site"
            />
          )
        }
        if (config.field === 'location_id') {
          if (!config.site_scope) {
            return <TextField size="small" disabled fullWidth placeholder="Pick a site scope above first" />
          }
          return (
            <SearchableSelect
              value={cs.value ?? ''}
              onChange={(id) => { const l = switchLocations.find((l: any) => l.id === id); onValue(id, { label: cs.label || l?.name || '' }) }}
              options={switchLocations}
              getValue={(l: any) => l.id}
              getLabel={(l: any) => l.name}
              placeholder="Pick a location"
            />
          )
        }
        if (config.field === 'priority') {
          return (
            <Select value={cs.value ?? ''} onChange={(e) => onValue(e.target.value, { label: cs.label || String(e.target.value) })} size="small" fullWidth displayEmpty>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          )
        }
        if (config.field === 'source') {
          return (
            <Select value={cs.value ?? ''} onChange={(e) => onValue(e.target.value, { label: cs.label || String(e.target.value) })} size="small" fullWidth displayEmpty>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="internal">Submitted inside the app</MenuItem>
              <MenuItem value="public_qr">Public QR scan</MenuItem>
              <MenuItem value="email">Inbound email</MenuItem>
              <MenuItem value="arcos_alarm">IoT alarm (Arcos)</MenuItem>
              <MenuItem value="checklist_follow_up">Checklist follow-up</MenuItem>
            </Select>
          )
        }
        if (config.field === 'work_type') {
          return (
            <Select value={cs.value ?? ''} onChange={(e) => onValue(e.target.value, { label: cs.label || String(e.target.value) })} size="small" fullWidth displayEmpty>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="reactive">Reactive</MenuItem>
              <MenuItem value="preventive">Preventive</MenuItem>
              <MenuItem value="inspection">Inspection</MenuItem>
              <MenuItem value="safety">Safety</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          )
        }
        return (
          <TextField size="small" placeholder="Match value" value={cs.value ?? ''}
            onChange={(e) => onValue(e.target.value)} fullWidth />
        )
      }
      return (<Stack spacing={2}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Route by field</Typography>
          <Select value={config.field ?? ''}
            onChange={(e) => onChange({ ...config, field: e.target.value, cases: [], site_scope: undefined })}
            displayEmpty size="small" fullWidth>
            <MenuItem value="">Select field...</MenuItem>
            <MenuItem value="site_id">Site</MenuItem>
            <MenuItem value="location_id">Location</MenuItem>
            <MenuItem value="priority">Priority</MenuItem>
            <MenuItem value="source">Source</MenuItem>
            <MenuItem value="work_type">Work type</MenuItem>
          </Select>
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>
            Cases are matched top-down; first match wins. Fallback is the "otherwise" branch.
          </Typography>
        </Box>

        {config.field === 'location_id' && (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Site scope</Typography>
            <SearchableSelect
              value={config.site_scope ?? ''}
              onChange={(id) => onChange({ ...config, site_scope: id, cases: [] })}
              options={sites}
              getValue={(s: any) => s.id}
              getLabel={formatSiteName}
              placeholder="Pick a site to load locations from"
            />
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>
              Locations are scoped to a single site. Changing this clears the cases.
            </Typography>
          </Box>
        )}

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Cases</Typography>
          <Stack spacing={1}>
            {cases.map((cs: any, i: number) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <Box sx={{ flex: 2 }}>{renderCaseValue(cs, i)}</Box>
                <TextField size="small" placeholder="Label (optional)" value={cs.label ?? ''}
                  onChange={(e) => setCase(i, { label: e.target.value })}
                  sx={{ flex: 1 }} />
                <IconButton size="small" onClick={() => { const next = [...cases]; next.splice(i, 1); set('cases', next) }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />}
              disabled={!config.field || (config.field === 'location_id' && !config.site_scope)}
              onClick={() => set('cases', [...cases, { value: '', label: '', steps: [] }])}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
              Add case
            </Button>
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 1 }}>
            Wire each case's actions from the outer step list — the switch itself is a router. A future update will let you nest steps inside cases directly.
          </Typography>
        </Box>
        <HintBox tone="info">
          <strong>How Switch works today:</strong> at run time the engine picks the first case whose value matches, and executes its case-scoped steps (currently empty for MVP — steps you add to the outer workflow all run for every case). Use the <strong>If/Else</strong> step if you need branching behaviour right now; use Switch to document the intended routing so we can auto-generate per-case branches once the nested editor lands.
        </HintBox>
      </Stack>)
    }

    case 'copy_request_attachments':
      return (<Stack spacing={1.5}>
        <Box sx={{ p: 2, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 13, mb: 0.5, fontWeight: 600 }}>Copy attachments from source request → work order</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.6 }}>
            No configuration needed.
          </Typography>
        </Box>
        <HintBox tone="info">
          <strong>When to use this instead of the Create WO checkbox:</strong> the Create WO step has a "Copy request attachments" toggle that runs at create time. Use this standalone step when the WO already exists (e.g. an earlier step attached to an existing WO), or when you want to copy attachments to multiple WOs from the same request.
        </HintBox>
      </Stack>)

    case 'update_request_status':
      return (<Stack spacing={2}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Action</Typography>
          <Select value={config.action ?? 'approve'} onChange={(e) => set('action', e.target.value)} size="small" fullWidth>
            <MenuItem value="approve">Approve the request</MenuItem>
            <MenuItem value="decline">Decline the request</MenuItem>
          </Select>
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>
            Runs the same service as the manual buttons — same audit, same customer emails.
          </Typography>
        </Box>
        {config.action === 'decline' && (
          <TextField label="Decline reason (optional)"
            value={config.reason ?? ''} onChange={(e) => set('reason', e.target.value)}
            size="small" fullWidth multiline minRows={2}
            placeholder="e.g. Duplicate of {{request_url}} — please consolidate."
            helperText="Shown to the requester in the decline email. Supports {{tokens}}." />
        )}
        {config.action === 'approve' && (
          <HintBox tone="info">
            Approving also auto-spawns a work order from the request (the "approve = create WO" behaviour of the manual approve button). If you already added a Create work order step earlier in this workflow, you'll end up with two WOs. Pick one — usually the Create work order step is more flexible.
          </HintBox>
        )}
        {config.action === 'decline' && (
          <HintBox tone="info">
            Declining is terminal — the request moves to <em>declined</em> state and the requester gets an email with the reason above. No WO is created.
          </HintBox>
        )}
      </Stack>)

    default: return null
  }
  })()

  if (!stepContent) return null
  // Fragment so the parent Stack's spacing controls layout — banner
  // gets a small margin below via HintBox padding + Stack gap.
  return (
    <Stack spacing={banner ? 2 : 0}>
      {banner}
      {stepContent}
    </Stack>
  )
}

/**
 * Schedule sub-section shared by the `create_work_order` and
 * `create_task` step configs (same "Same UI for both" convention as
 * the rest of that case block — see `kindLabel` below). Mirrors the
 * Schedule section in the main Create WO/Task dialog so workflows can
 * produce scheduled rows (one-shot or recurring template) instead of
 * always-immediate ones.
 *
 * State lives on the workflow step's `config` object:
 *   - `config.schedule_mode`: 'off' | 'one_shot' | 'recurring'
 *   - `config.scheduled_for`: ISO string (one-shot)
 *   - `config.schedule_config`: { type, time, days?, day_of_month?, month?, tz }
 *
 * The workflow engine reads these and forwards them to createWorkOrder()/createTask().
 */
function WorkflowScheduleSection({ config, set, onChange, kindLabel }: {
  config: Record<string, any>
  set: (k: string, v: any) => void
  onChange: (c: Record<string, any>) => void
  kindLabel: string
}) {
  const { prefs } = useOrgSettings()
  const orgTz = prefs.timezone || 'UTC'
  const tzLabel = `in ${shortTzLabel(orgTz)}`
  const mode: 'off' | 'one_shot' | 'recurring' = (config.schedule_mode ?? 'off') as any
  const sc = (config.schedule_config && typeof config.schedule_config === 'object')
    ? config.schedule_config : {}

  // Wall-clock string → ISO (interpreted in org tz). The engine will
  // forward this to createWorkOrder as the absolute scheduled_for.
  const toIso = (wall: string): string | null => {
    if (!wall) return null
    const d = tzWallToUtc(wall, orgTz)
    return d ? d.toISOString() : null
  }
  const fromIso = (iso?: string): string => {
    if (!iso) return ''
    try {
      const d = new Date(iso)
      const p = new Intl.DateTimeFormat('en-CA', {
        timeZone: orgTz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      }).formatToParts(d)
      const g = (t: string) => p.find((x) => x.type === t)?.value ?? '00'
      return `${g('year')}-${g('month')}-${g('day')}T${g('hour') === '24' ? '00' : g('hour')}:${g('minute')}`
    } catch { return '' }
  }

  function setMode(next: 'off' | 'one_shot' | 'recurring') {
    if (next === 'off') {
      // Clear both — keep config tidy
      const { schedule_mode: _m, scheduled_for: _f, schedule_config: _c, ...rest } = config
      onChange({ ...rest, schedule_mode: 'off' })
      return
    }
    if (next === 'recurring' && !sc.type) {
      onChange({
        ...config,
        schedule_mode: 'recurring',
        scheduled_for: undefined,
        schedule_config: { type: 'daily', time: '09:00', days: ['mon', 'tue', 'wed', 'thu', 'fri'], tz: orgTz },
      })
      return
    }
    onChange({ ...config, schedule_mode: next })
  }

  function updateCfg(patch: Record<string, any>) {
    onChange({ ...config, schedule_config: { ...sc, tz: orgTz, ...patch } })
  }

  return (<>
    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Schedule</Typography>
    <Select value={mode} onChange={(e) => setMode(e.target.value as any)} size="small" fullWidth>
      <MenuItem value="off">Create immediately when workflow runs</MenuItem>
      <MenuItem value="one_shot">Schedule for a specific date/time</MenuItem>
      <MenuItem value="recurring">Recurring (calendar-based)</MenuItem>
    </Select>

    {mode === 'one_shot' && (
      <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
          Activate the {kindLabel} at
        </Typography>
        <TextField
          type="datetime-local"
          value={fromIso(config.scheduled_for)}
          onChange={(e) => set('scheduled_for', toIso(e.target.value))}
          size="small" fullWidth />
        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>
          Interpreted {tzLabel}. The {kindLabel} will sit in the "Scheduled" section
          until ~10 min before this time.
        </Typography>
      </Box>
    )}

    {mode === 'recurring' && (
      <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>Every</Typography>
            <Select value={sc.type ?? 'daily'} onChange={(e) => updateCfg({ type: e.target.value })} size="small" sx={{ flex: 1 }}>
              <MenuItem value="daily">day</MenuItem>
              <MenuItem value="weekly">week (specific days)</MenuItem>
              <MenuItem value="monthly">month</MenuItem>
              <MenuItem value="quarterly">quarter</MenuItem>
              <MenuItem value="yearly">year</MenuItem>
            </Select>
          </Stack>
          {sc.type === 'weekly' && (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 1 }}>Days</Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((d) => {
                  const active = (sc.days ?? []).includes(d)
                  return (
                    <Box key={d} onClick={() => {
                      const next = active ? (sc.days ?? []).filter((x: string) => x !== d) : [...(sc.days ?? []), d]
                      updateCfg({ days: next })
                    }} sx={{
                      width: 36, height: 36, borderRadius: '50%',
                      border: '1px solid', borderColor: active ? '#0D9488' : 'divider',
                      bgcolor: active ? '#0D9488' : 'transparent',
                      color: active ? '#fff' : 'text.primary',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize',
                    }}>{d.slice(0, 1).toUpperCase()}</Box>
                  )
                })}
              </Stack>
            </Box>
          )}
          {(sc.type === 'monthly' || sc.type === 'quarterly') && (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>On day</Typography>
              <Select value={sc.day_of_month ?? 1} onChange={(e) => updateCfg({ day_of_month: Number(e.target.value) })} size="small" sx={{ width: 80 }}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
              <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>(clamps to last day for shorter months)</Typography>
            </Stack>
          )}
          {sc.type === 'yearly' && (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>In</Typography>
              <Select value={sc.month ?? 1} onChange={(e) => updateCfg({ month: Number(e.target.value) })} size="small" sx={{ flex: 1 }}>
                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                ))}
              </Select>
              <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>on day</Typography>
              <Select value={sc.day_of_month ?? 1} onChange={(e) => updateCfg({ day_of_month: Number(e.target.value) })} size="small" sx={{ width: 70 }}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </Stack>
          )}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>At</Typography>
            <TextField type="time" value={sc.time ?? '09:00'} onChange={(e) => updateCfg({ time: e.target.value })}
              size="small" sx={{ width: 130 }} inputProps={{ step: 300 }} />
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{tzLabel}</Typography>
          </Stack>
        </Stack>
      </Box>
    )}
  </>)
}
